/**
 * TREATX-SEARCH-V2-FINAL-01 — Final SEARCH domain closure experience.
 *
 * Strict invariants (per owner directive):
 *   - Canonical data only — reads from `/api/search/providers`,
 *     `/api/patient/shortlist`, `/api/media/<id>`.
 *   - No new SSOT, no new endpoint, no parallel projection.
 *   - Sole canonical search implementation (V1 retired 2026-02 via
 *     TREATX-SEARCH-V1-FINAL-RETIRE-01). Lives at `/search-v2`.
 *   - All right-panel tabs are treatment-aware.
 *   - DoctorDetailModal (S04 + DOCTOR-MODAL-01 canonical) is reused
 *     verbatim — the same component the classic search uses.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useSurfaceRule } from '../context/SurfaceContext';
import api from '../utils/api';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import {
  Search, MapPin, Award, Users, Globe, Tag, ShieldCheck, X, Filter,
  Heart, ChevronLeft, ChevronRight, ChevronDown, ZoomIn, Maximize2, Minimize2, ExternalLink,
  Stethoscope, Star, Building2, ImageIcon, FileText, Plus, Minus,
  CheckCircle2, ArrowLeft, GitCompare, LayoutGrid, Rows3, Info, RotateCcw,
  Plane, BadgeCheck, Loader2, Check,
} from 'lucide-react';
// PRD1.9 — canonical provider-type enumeration shared with onboarding.
// The same constant powers the Provider Type chooser in
// /app/frontend/src/pages/provider/ProviderOnboarding.jsx, so the labels
// surfaced to a patient are guaranteed to match what a provider sees when
// they self-classify during registration.
import { PROVIDER_TYPES } from '../context/ProviderOnboardingContext';
// TCPN-CN1 — single canonical country normaliser (already used by V1).
// Used by V2's Destination dispatcher to route country-like values to the
// backend `country=<ISO2>` parameter while keeping unrecognised values on
// the existing `city=<value>` path.
import { normalizeCountryToISO2, iso2ToCountryName } from '../utils/countryNormalize';
import DoctorDetailModal from '../components/DoctorDetailModal';
import { CampaignTeaser } from '../components/CampaignRenderers/CanonicalCampaignRenderers';
import { useToast } from '../hooks/use-toast';
import { ResidencePromptDialog } from '../components/patient/ResidencePrompt';

// SEARCH-V2-REQUEST-QUOTE-PARITY-01 — V2 quote-request shell constants.
// V2-isolated sessionStorage keys + V2-specific anonymous redirect.
// Mirror V1's PATIENT_INQUIRY_NOTE_MAX (200) verbatim.
const PATIENT_INQUIRY_NOTE_MAX_V2 = 200;
const SESSION_STORAGE_KEY_SELECTION_V2 = 'selectedProvidersV2';
const SESSION_STORAGE_KEY_NOTE_V2 = 'patientInquiryNoteV2';
const SESSION_STORAGE_KEY_TREATMENT_V2 = 'filtersTreatmentV2';
const SESSION_STORAGE_KEY_PACKAGE_INTENT_V2 = 'pendingPackageByClinicV2';
const ANON_REDIRECT_V2 = '/login?redirect=/search-v2';

const API = process.env.REACT_APP_BACKEND_URL;
const fmt = (s) => String(s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

// SV2-OFFER-SCOPE-UNIFY-01 — ONE canonical treatment-slug normalisation for the
// whole Search surface, mirroring the server's `normalize_treatment_slug`
// (trim + lowercase + `_`→`-`). Four places used to decide "is this offer for
// the searched treatment?" and only `useTreatmentCampaigns` folded underscores:
// an alias deep-link (`?treatment=hair_transplant_fue_dhi`) therefore showed the
// offer badge on the card while the offers tab, its count and the comparison row
// all reported none. Matching stays EXACT — this only normalises the spelling of
// the two slugs being compared, it never widens to a related treatment.
export const normalizeTreatmentSlug = (s) => String(s || '').trim().toLowerCase().replace(/_/g, '-');

// SV2-ACCEPT-01 — the patient must read ONE name for the searched treatment.
// `fmt(slug)` re-cases the slug ("Hair Transplant Fue Dhi") while the active
// filter chip already resolves the canonical catalogue name ("Hair Transplant
// (FUE/DHI)"), so a single screen showed the same treatment under two spellings.
// Resolves from the taxonomy already loaded by the page; `fmt` stays the
// fallback so a missing/slow taxonomy never blanks the label.
const treatmentNameOf = (taxonomy, slug) => {
  if (!slug) return '';
  const want = normalizeTreatmentSlug(slug);
  const hit = (taxonomy?.treatments || []).find(
    (x) => normalizeTreatmentSlug(x.slug || x) === want,
  );
  return (hit && (hit.name || hit.display_name)) || fmt(slug);
};
const eur = (n) => (typeof n === 'number' ? `€${n.toLocaleString('en-US')}` : '—');

// SEARCH-V2-TRIP-MODE-01 — single-select Trip Mode filter.
// Enum values mirror the canonical backend contract (see
// /app/backend/routes/search_routes.py & compliance_summary_service.py).
// No "Any" pseudo-option: re-clicking the active option clears the filter,
// and the global Clear All also clears it.
const TRIP_MODES = [
  { id: 'treatment_only', name: 'Treatment Only', description: 'Clinic visit only — patient arranges travel and stay.' },
  { id: 'treatment_plus_travel', name: 'Treatment + Travel', description: 'Treatment bundled with travel logistics.' },
  { id: 'vip_full_support', name: 'VIP Full Support', description: 'Full concierge package: travel, stay, transfers, aftercare.' },
];
const TRIP_MODE_LABEL = TRIP_MODES.reduce((acc, m) => { acc[m.id] = m.name; return acc; }, {});

// SEARCH-V2-FINAL-01 — public route.  Auth is OPTIONAL and only used
// for the canonical persistent shortlist (S05).  We read the unified
// token directly from localStorage so this page renders identically
// regardless of which AuthProvider state has hydrated.  No throw,
// no redirect, no hidden guard.
// BUG A ROOT CAUSE FIX (2026-08-21) — cookie-migration cutover regression.
//
// Post FIND-H-08 the JWT lives in an HttpOnly `treatx_auth` cookie that
// JS cannot read. The prior implementation returned `null` unconditionally,
// which made `isAuth = !!token` permanently `false` even for logged-in
// patients. Consequence: the "Request offers" CTA always took the
// anonymous branch and navigated to `/login?redirect=/search-v2` — the
// public landing/login page — WITHOUT ever POSTing the request. Backend
// therefore never persisted the lead, so `Your Requests` correctly
// showed 0 (nothing was sent) and no provider ever saw the request.
//
// The canonical client-readable session signals in the cookie era are:
//   * `localStorage['treatx_unified_user']`  — user snapshot mirror
//   * `document.cookie` contains `treatx_csrf=`  — CSRF cookie is
//     readable by JS (only `treatx_auth` is HttpOnly).
// Either being present means a logged-in session was hydrated. Both are
// the SAME signals the shared axios interceptor uses to distinguish
// anonymous vs expired sessions. No new auth authority.
const _readUnifiedToken = () => {
  try {
    if (typeof localStorage !== 'undefined'
        && localStorage.getItem('treatx_unified_user')) {
      return 'cookie-session';
    }
    if (typeof document !== 'undefined'
        && (document.cookie || '').includes('treatx_csrf=')) {
      return 'cookie-session';
    }
    return null;
  } catch (_) { return null; }
};

// ── Destination Typeahead ──────────────────────────────────────────────
// Single-input picker that commits an explicit axis (country | city) on
// suggestion select.  Typing alone never fires a backend request.  After
// commit, the parent's `filters.country` / `filters.city` are set; the
// dispatcher just forwards them to the canonical `country=` / `city=`
// query params.  Mutual exclusion per owner amendment: selecting a new
// destination replaces the previous one (country ↔ city never co-exist).
// SRCH-LOC-CANON-01 — canonical destination identity, byte-for-byte the same
// rule as the backend `fold_location_value()` in
// services/compliance_summary_service.py. `location.city` is still free text
// (no city reference dataset exists yet — see the owner decision recorded in
// memory/SEARCH_V2_LOCATION_CANONICAL_01.md), so "Izmir", "İzmir", "izmir",
// "IZMIR" and "ızmir" must collapse to ONE suggestion while the matching path
// keeps accepting every stored spelling. Turkish letters are mapped
// explicitly: Unicode folding alone leaves dotless 'ı' distinct from 'i'.
const _TR_FOLD = { 'İ': 'i', 'I': 'i', 'ı': 'i', 'Ş': 's', 'ş': 's', 'Ğ': 'g', 'ğ': 'g', 'Ü': 'u', 'ü': 'u', 'Ö': 'o', 'ö': 'o', 'Ç': 'c', 'ç': 'c' };
export const foldLocationValue = (value) => String(value || '')
  .replace(/[İIıŞşĞğÜüÖöÇç]/g, (ch) => _TR_FOLD[ch] || ch)
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .toLowerCase()
  .trim()
  .replace(/\s+/g, ' ');

function DestinationTypeahead({ countryCode, cityName, onSelect, onClear }) {
  const { t } = useTranslation();
  const [countries, setCountries] = useState([]);  // [{iso, label}]
  const [cities, setCities] = useState([]);        // [{name, label}]
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = React.useRef(null);

  // Selected label shown in the input when not actively typing.
  const selectedLabel = countryCode
    ? (iso2ToCountryName(countryCode) || countryCode)
    : (cityName || '');

  // Fetch the two discovery endpoints ONCE on mount.  Failures are caught
  // independently so a partial outage still lets the other group render.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/search/countries`);
        if (!cancelled) {
          const raw = r.data?.countries || [];
          const seen = new Set();
          const out = [];
          for (const c of raw) {
            const iso = normalizeCountryToISO2(c);
            if (!iso || seen.has(iso)) continue;
            seen.add(iso);
            out.push({ iso, label: iso2ToCountryName(iso) || iso });
          }
          out.sort((a, b) => a.label.localeCompare(b.label));
          setCountries(out);
        }
      } catch (_) { /* canonical empty state */ }
      try {
        const r = await axios.get(`${API}/api/search/cities`);
        if (!cancelled) {
          const raw = r.data?.cities || [];
          // One option per canonical identity. The backend already collapses
          // these; this mirrors it so a stale cache or a future writer can
          // never resurface "Izmir" AND "İzmir" as two destinations.
          const seenCity = new Set();
          const out = [];
          for (const c of raw) {
            if (typeof c !== 'string' || !c.trim()) continue;
            const key = foldLocationValue(c);
            if (!key || seenCity.has(key)) continue;
            seenCity.add(key);
            out.push({ name: c, label: c });
          }
          out.sort((a, b) => a.label.localeCompare(b.label));
          setCities(out);
        }
      } catch (_) { /* canonical empty state */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Canonical identity, shared with the suggestion de-duplication above so
  // typing and de-duplication can never disagree (`Türkiye` ≈ `Turkey`,
  // `ızmir` ≈ `İzmir`).
  const _norm = foldLocationValue;

  const q = _norm(query);
  const matchedCountries = q
    ? countries.filter((c) => _norm(c.label).includes(q))
    : countries;
  const matchedCities = q
    ? cities.filter((c) => _norm(c.label).includes(q))
    : cities;
  // Flat list for keyboard navigation: countries first, then cities.
  const flat = [
    ...matchedCountries.map((c) => ({ kind: 'country', iso: c.iso, label: c.label })),
    ...matchedCities.map((c) => ({ kind: 'city', name: c.name, label: c.label })),
  ];

  // Close on outside click.
  useEffect(() => {
    if (!isOpen) return;
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [isOpen]);

  const commit = (entry) => {
    if (!entry) return;
    // Owner amendment: mutual exclusion — selecting one axis replaces
    // the other.  Parent's onSelect receives a single new selection;
    // it is responsible for nulling the opposite axis.
    onSelect(entry);
    setQuery('');
    setIsOpen(false);
  };

  const onKeyDown = (e) => {
    if (!isOpen) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, Math.max(0, flat.length - 1)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (flat.length > 0) commit(flat[Math.min(highlight, flat.length - 1)]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setIsOpen(false);
      setQuery('');
    }
  };

  // Reset highlight whenever the matched list shape changes.
  useEffect(() => { setHighlight(0); }, [q]);

  // Input value is either the active typing buffer or the committed
  // selection label (when not actively typing).
  const inputValue = isOpen ? query : selectedLabel;

  return (
    <div className="relative" ref={wrapRef}>
      <Input
        value={inputValue}
        onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
        onFocus={() => setIsOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={t('searchV2.filters.destinationPlaceholder')}
        className="text-sm h-8"
        data-testid="search-v2-filter-destination"
        autoComplete="off"
      />
      {(countryCode || cityName) && !isOpen && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onClear(); setQuery(''); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-500"
          aria-label={t('searchV2.filters.clearDestination')}
          data-testid="search-v2-filter-destination-clear"
        ><X className="h-3 w-3" /></button>
      )}
      {isOpen && (
        <div
          className="absolute z-30 mt-1 left-0 right-0 bg-white border border-slate-200 rounded-md shadow-lg max-h-80 overflow-y-auto"
          data-testid="search-v2-destination-suggestions"
        >
          {flat.length === 0 ? (
            <div
              className="px-3 py-2 text-[12px] italic text-slate-400"
              data-testid="search-v2-destination-empty"
            >{t('searchV2.filters.noDestinationMatch')}</div>
          ) : (
            <>
              {matchedCountries.length > 0 && (
                <div>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50">Countries</div>
                  {matchedCountries.map((c, idx) => {
                    const flatIdx = idx;  // countries appear first in flat
                    const active = flatIdx === highlight;
                    return (
                      <button
                        key={`co-${c.iso}`}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); commit({ kind: 'country', iso: c.iso, label: c.label }); }}
                        onMouseEnter={() => setHighlight(flatIdx)}
                        className={`w-full text-left px-3 py-1.5 text-sm ${active ? 'bg-[#3BA4C5]/10 text-[#0E2A47]' : 'hover:bg-slate-50 text-slate-700'}`}
                        data-testid={`search-v2-destination-suggestion-country-${c.iso}`}
                      >{c.label}</button>
                    );
                  })}
                </div>
              )}
              {matchedCities.length > 0 && (
                <div>
                  <div className="px-3 pt-2 pb-1 text-[10px] uppercase tracking-wider font-bold text-slate-400 bg-slate-50">Cities</div>
                  {matchedCities.map((c, idx) => {
                    const flatIdx = matchedCountries.length + idx;
                    const active = flatIdx === highlight;
                    return (
                      <button
                        key={`ci-${c.name}`}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); commit({ kind: 'city', name: c.name, label: c.label }); }}
                        onMouseEnter={() => setHighlight(flatIdx)}
                        className={`w-full text-left px-3 py-1.5 text-sm ${active ? 'bg-[#3BA4C5]/10 text-[#0E2A47]' : 'hover:bg-slate-50 text-slate-700'}`}
                        data-testid={`search-v2-destination-suggestion-city-${c.name}`}
                      >{c.label}</button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Filter Rail ────────────────────────────────────────────────────────
const SECTION_STYLE = 'mb-4';
const SECTION_TITLE = 'text-[11px] font-semibold text-slate-500 uppercase tracking-wider mb-2';

// ── SEARCH-V2-MULTISELECT-01 ───────────────────────────────────────────
// Reusable searchable multi-select used by Languages / Accreditations /
// Doctor Specialty.  Pure presentational wrapper around the existing
// `filters.*` state and `toggleArr` mutator — selection state is NEVER
// owned here.  Clearing flows through the existing Active-Filter chips
// and global Clear All; this component handles select / deselect only.
function _diaNorm(s) {
  return String(s || '').normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase();
}
function SearchableMultiSelect({
  label,
  testIdRoot,
  options,
  selectedSlugs,
  onToggle,
  loadingText,
  errorText,
  taxonomyError,
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Source ordering preserved: options array is rendered & filtered in place.
  const visible = useMemo(() => {
    if (!query) return options;
    const q = _diaNorm(query);
    return options.filter((o) => _diaNorm(o.label).includes(q));
  }, [options, query]);

  // Outside click → close
  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Autofocus search input when opened
  useEffect(() => {
    if (open && inputRef.current) {
      // Defer to next tick so the input is mounted.
      const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
      return () => clearTimeout(id);
    }
    return undefined;
  }, [open]);

  // Resolve the leading selected label in source order (deterministic).
  const firstSelectedLabel = useMemo(() => {
    if (!selectedSlugs || selectedSlugs.length === 0) return null;
    const sel = new Set(selectedSlugs);
    for (const o of options) if (sel.has(o.slug)) return o.label;
    return null;
  }, [options, selectedSlugs]);

  const count = (selectedSlugs || []).length;
  const collapsedLabel =
    count === 0
      ? `Select ${label}`
      : count === 1
      ? (firstSelectedLabel || _fmt1(selectedSlugs[0]))
      : `${firstSelectedLabel || _fmt1(selectedSlugs[0])} +${count - 1}`;

  const isEmpty = options.length === 0;
  const disabled = isEmpty;

  return (
    <div className="relative" ref={rootRef} onKeyDown={(e) => { if (e.key === 'Escape') setOpen(false); }}>
      <button
        type="button"
        disabled={disabled}
        aria-expanded={open}
        aria-haspopup="listbox"
        onClick={() => { if (!disabled) setOpen((v) => !v); }}
        className={`w-full inline-flex items-center justify-between gap-2 text-[12px] px-2.5 py-1.5 rounded-md border bg-white ${
          disabled
            ? 'border-slate-200 text-slate-400 cursor-not-allowed'
            : count > 0
            ? 'border-slate-300 text-slate-800 hover:border-slate-400'
            : 'border-slate-200 text-slate-600 hover:border-slate-300'
        }`}
        data-testid={`${testIdRoot}-trigger`}
      >
        <span className="truncate">{collapsedLabel}</span>
        <ChevronDown className={`h-3.5 w-3.5 flex-none transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {isEmpty && (
        <p className="text-[11px] text-slate-400 italic mt-1" data-testid={`${testIdRoot}-empty`}>
          {taxonomyError ? errorText : loadingText}
        </p>
      )}
      {open && !isEmpty && (
        <div
          className="absolute left-0 right-0 z-30 mt-1 bg-white border border-slate-200 rounded-md shadow-lg overflow-hidden"
          role="listbox"
          aria-multiselectable="true"
          data-testid={`${testIdRoot}-panel`}
        >
          <div className="p-1.5 border-b border-slate-100">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t('searchV2.filters.searchPlaceholder')}
              className="w-full text-[12px] px-2 py-1 border border-slate-200 rounded outline-none focus:border-slate-400"
              data-testid={`${testIdRoot}-search`}
            />
          </div>
          <ul className="max-h-[280px] overflow-y-auto py-0.5">
            {visible.length === 0 ? (
              <li className="px-2 py-1.5 text-[12px] text-slate-400 italic" data-testid={`${testIdRoot}-no-matches`}>
                {t('searchV2.filters.noMatches')}
              </li>
            ) : (
              visible.map((o) => {
                const checked = (selectedSlugs || []).includes(o.slug);
                return (
                  <li
                    key={o.slug}
                    role="option"
                    aria-selected={checked}
                    onClick={() => onToggle(o.slug)}
                    className={`flex items-center gap-2 px-2 py-1 text-[12px] cursor-pointer leading-snug ${
                      checked ? 'bg-slate-50 text-slate-900' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`${testIdRoot}-option-${o.slug}`}
                    title={o.title || o.label}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      readOnly
                      tabIndex={-1}
                      className="h-3.5 w-3.5 accent-emerald-600 pointer-events-none flex-none"
                    />
                    <span className="truncate">{o.label}</span>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
// Local fmt fallback that doesn't shadow the file-level `fmt` (used only
// when an option slug is somehow not in the options list — defensive).
const _fmt1 = (s) => String(s || '').replace(/[-_]/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export function FilterRail({ filters, setFilters, taxonomy, onRetryTaxonomy }) {
  const { t } = useTranslation();
  const campaignsAllowed = useSurfaceRule('campaigns');
  const setF = (k, v) => setFilters((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setFilters((p) => {
    const cur = new Set(p[k] || []);
    cur.has(v) ? cur.delete(v) : cur.add(v);
    return { ...p, [k]: Array.from(cur) };
  });

  const treatments = useMemo(() => taxonomy?.treatments || [], [taxonomy?.treatments]);
  const accs = taxonomy?.accreditations || [];
  const langs = taxonomy?.languages || [];
  const taxonomyError = taxonomy?.error === true;

  // P3-D1 / P3-D2 — group the master catalogue by its canonical category and
  // separate the treatments no provider currently offers. Both lists are
  // derived from canonical catalogues only; neither filters the picker.
  const offeredSet = useMemo(
    () => new Set(taxonomy?.offeredTreatments || []),
    [taxonomy?.offeredTreatments],
  );
  // SRCH-V2-CATEGORY-01 — the selected category narrows which treatments are
  // offered to the patient. It is NOT a provider filter: the canonical search
  // API has no category parameter (verified against
  // `ComplianceSummaryService.search`), and the request builder never sends it.
  const selectedCategory = filters.category || null;
  const categoryOptions = useMemo(
    () => (taxonomy?.categories || []).filter((c) => (c.slug && c.label)),
    [taxonomy?.categories],
  );
  // A deep-linked treatment whose category cannot be resolved from the
  // canonical taxonomy keeps its selection and says so, rather than being
  // silently discarded.
  const treatmentCategory = useMemo(() => {
    if (!filters.treatment) return null;
    // SV2-OFFER-SCOPE-UNIFY-01 — canonical comparison, so an alias
    // deep-link is not reported as "not mapped to a category".
    const want = normalizeTreatmentSlug(filters.treatment);
    const hit = (taxonomy?.treatments || []).find(
      (x) => normalizeTreatmentSlug(x.slug) === want,
    );
    return hit?.category_slug || null;
  }, [filters.treatment, taxonomy?.treatments]);
  // SV2-OFFER-SCOPE-UNIFY-01 — the <select> is controlled by the exact option
  // value, so an alias deep-link must resolve to the canonical option or the
  // picker renders blank while the rest of the page shows the treatment.
  const selectedTreatmentValue = useMemo(() => {
    if (!filters.treatment) return '';
    const want = normalizeTreatmentSlug(filters.treatment);
    const hit = (taxonomy?.treatments || []).find(
      (x) => normalizeTreatmentSlug(x.slug || x) === want,
    );
    return (hit && (hit.slug || hit)) || filters.treatment;
  }, [filters.treatment, taxonomy?.treatments]);
  const treatmentUnmapped = !!filters.treatment
    && (taxonomy?.treatments || []).length > 0
    && !treatmentCategory;
  const inCategory = useCallback(
    (tx) => !selectedCategory || tx.category_slug === selectedCategory,
    [selectedCategory],
  );

  const offeredByCategory = useMemo(() => {
    const cats = (taxonomy?.categories || []).filter((c) => !selectedCategory || c.slug === selectedCategory);
    // If the provider-enabled list is unavailable, fall back to grouping the
    // full master catalogue — the picker must never render empty.
    const scoped = treatments.filter(inCategory);
    const offered = offeredSet.size === 0 ? scoped : scoped.filter((t) => offeredSet.has(t.slug));
    const known = cats
      .map((c) => ({ ...c, items: offered.filter((t) => t.category_slug === c.slug) }))
      .filter((c) => c.items.length > 0);
    const orphans = offered.filter((t) => !cats.find((c) => c.slug === t.category_slug));
    return (orphans.length > 0 && !selectedCategory)
      ? [...known, { slug: 'other', label: t('searchV2.filters.otherGroup'), items: orphans }]
      : known;
  }, [treatments, offeredSet, taxonomy?.categories, selectedCategory, inCategory, t]);
  const notOfferedTreatments = useMemo(
    () => (offeredSet.size === 0
      ? []
      : treatments.filter(inCategory).filter((tx) => !offeredSet.has(tx.slug))),
    [treatments, offeredSet, inCategory],
  );
  // SRCH-V3-PROVIDER-CENTRIC-01 — `offeredSet` comes from
  // `/api/public/treatments?provider_enabled=true`, which is
  // catalogue-published INTERSECT provider-offered. Under the Owner's
  // provider-centric decision the catalogue is NOT an eligibility authority, so
  // absence from that set does NOT prove no provider offers the treatment —
  // `dental-veneers` has 23 eligible providers while having no catalogue row.
  // The claim is therefore only made for treatments the catalogue DOES carry;
  // for catalogue-absent treatments the honest statement is the unmapped note.
  const treatmentInCatalogue = !!filters.treatment
    && treatments.some((tx) => tx.slug === filters.treatment);
  const treatmentNotOffered = !!filters.treatment
    && offeredSet.size > 0
    && treatmentInCatalogue
    && !offeredSet.has(filters.treatment);

  return (
    <aside
      className="w-72 flex-none h-full border-r border-slate-200 bg-white overflow-y-auto shadow-[1px_0_0_0_rgba(15,23,42,0.04)]"
      data-testid="search-v2-filter-rail"
    >
      {/* P1 §6 — every panel carries the SAME header construction (icon +
          eyebrow + title + bottom rule) so the three regions read as peers.
          Sticky, so the panel identity survives scrolling. */}
      <div className="sticky top-0 z-20 flex items-center gap-2 bg-white/95 backdrop-blur-sm border-b border-slate-200 px-4 py-2.5">
        <Filter className="h-4 w-4 text-slate-400 flex-none" />
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">{t('searchV2.refine')}</p>
          <h2 className="font-semibold text-slate-800 text-sm">{t('searchV2.panels.filters')}</h2>
        </div>
      </div>
      <div className="p-4">

      {/* SRCH-V2-TAXONOMY-RESILIENCE-01 — optional catalogues that failed are
          HIDDEN (below) and disclosed here, never rendered as empty lists that
          look like legitimate zero-option catalogues. */}
      {(taxonomy?.degraded || []).length > 0 && !taxonomyError && (
        <div
          className="mb-3 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-800"
          data-testid="search-v2-filter-degraded-notice"
        >
          {t('searchV2.taxonomy.optionalUnavailable')}
        </div>
      )}
      {taxonomy?.offeringsUnavailable === true && !taxonomyError && (
        <div
          className="mb-3 px-2 py-1.5 bg-amber-50 border border-amber-200 rounded-md text-[11px] text-amber-800"
          data-testid="search-v2-filter-offerings-notice"
        >
          {t('searchV2.taxonomy.offeringsUnavailable')}
        </div>
      )}

      {/* P0 §4 — Category sits immediately ABOVE Treatment. Options come from
          the canonical treatment taxonomy (`treatment_categories` via
          `/api/public/treatment-taxonomy`), never hardcoded. */}
      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.category.label')}</p>
        <select
          value={selectedCategory || ''}
          onChange={(e) => {
            const next = e.target.value || null;
            setFilters((prev) => {
              // Clear the treatment ONLY when it does not belong to the newly
              // selected category. An unmapped deep-linked treatment is kept.
              const hit = (taxonomy?.treatments || []).find((x) => x.slug === prev.treatment);
              const keep = !next || !prev.treatment || !hit || hit.category_slug === next;
              return { ...prev, category: next, treatment: keep ? prev.treatment : null };
            });
          }}
          className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white"
          data-testid="search-v2-filter-category"
          disabled={taxonomyError}
        >
          <option value="">{t('searchV2.category.all')}</option>
          {categoryOptions.map((c) => (
            <option key={c.slug} value={c.slug}>{c.label}</option>
          ))}
        </select>
      </div>

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.treatment')}</p>
        {taxonomyError && (
          <div
            className="mb-1.5 px-2 py-1.5 bg-red-50 border border-red-200 rounded-md text-[11px] text-red-700"
            data-testid="search-v2-filter-treatment-error"
          >
            <p>{t('searchV2.taxonomy.coreUnavailable')}</p>
            {onRetryTaxonomy && (
              <button
                type="button"
                onClick={onRetryTaxonomy}
                className="mt-1 inline-flex items-center gap-1 font-semibold underline underline-offset-2 hover:text-red-900"
                data-testid="search-v2-taxonomy-retry"
              >
                <RotateCcw className="h-3 w-3" aria-hidden="true" />{t('searchV2.taxonomy.retry')}
              </button>
            )}
          </div>
        )}
        <select
          value={selectedTreatmentValue}
          onChange={(e) => setF('treatment', e.target.value || null)}
          className="w-full text-sm border border-slate-200 rounded-md px-2.5 py-1.5 bg-white"
          data-testid="search-v2-filter-treatment"
          disabled={taxonomyError}
        >
          <option value="">
            {selectedCategory ? t('searchV2.filters.anyTreatmentInCategory') : t('searchV2.filters.anyTreatment')}
          </option>
          {/* STABILIZATION-01: synthetic <option> so the controlled
              <select> resolves to `filters.treatment` from the URL
              even before the async taxonomy fetch returns.  Once
              taxonomy resolves, the canonical option below supersedes
              this one (same value, refined label). */}
          {filters.treatment && !treatments.find(
            (t) => normalizeTreatmentSlug(t.slug || t) === normalizeTreatmentSlug(filters.treatment),
          ) && (
            <option value={filters.treatment}>{fmt(filters.treatment)}</option>
          )}
          {/* P3-D1 / P3-D2 — the 64-entry master catalogue is grouped by its
              canonical category, and the treatments no provider currently
              offers are separated out instead of silently returning an
              empty result set. NOTHING is removed from the picker: every
              master treatment stays selectable, and the deep-link
              fallback above is untouched. */}
          {offeredByCategory.map((group) => (
            <optgroup key={group.slug} label={group.label} data-testid={`search-v2-treatment-group-${group.slug}`}>
              {group.items.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </optgroup>
          ))}
          {notOfferedTreatments.length > 0 && (
            <optgroup label={t('searchV2.filters.notOfferedGroup')} data-testid="search-v2-treatment-group-unoffered">
              {notOfferedTreatments.map((t) => (
                <option key={t.slug} value={t.slug}>{t.name}</option>
              ))}
            </optgroup>
          )}
        </select>
        {treatmentUnmapped && (
          <p className="mt-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1" data-testid="search-v2-treatment-unmapped-note">
            {t('searchV2.category.unmappedTreatment')}
          </p>
        )}
        {selectedCategory && !filters.treatment && offeredByCategory.length === 0 && notOfferedTreatments.length === 0 && (
          <p className="mt-1.5 text-[11px] text-slate-600 bg-slate-50 border border-slate-200 rounded-md px-2 py-1" data-testid="search-v2-category-no-treatments">
            {t('searchV2.category.noTreatments')}
          </p>
        )}
        {treatmentNotOffered && (
          <p className="mt-1.5 text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="search-v2-treatment-not-offered-note">
            {t('searchV2.filters.notOfferedNote')}
          </p>
        )}
      </div>

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.destination')}</p>
        <DestinationTypeahead
          countryCode={filters.country || null}
          cityName={filters.city || null}
          onSelect={(entry) => {
            // Owner amendment: mutual exclusion.  Selecting a country
            // clears any city; selecting a city clears any country.
            if (entry.kind === 'country') {
              setFilters((p) => ({ ...p, country: entry.iso, city: null }));
            } else if (entry.kind === 'city') {
              setFilters((p) => ({ ...p, city: entry.name, country: null }));
            }
          }}
          onClear={() => setFilters((p) => ({ ...p, country: null, city: null }))}
        />
      </div>

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.budget')}</p>
        <div className="flex items-center gap-2">
          <Input
            type="number"
            value={filters.budget_min ?? ''}
            onChange={(e) => setF('budget_min', e.target.value ? Number(e.target.value) : null)}
            placeholder={t('searchV2.filters.min')}
            className="text-sm h-8"
            data-testid="search-v2-filter-budget-min"
          />
          <Input
            type="number"
            value={filters.budget_max ?? ''}
            onChange={(e) => setF('budget_max', e.target.value ? Number(e.target.value) : null)}
            placeholder={t('searchV2.filters.max')}
            className="text-sm h-8"
            data-testid="search-v2-filter-budget-max"
          />
        </div>
      </div>

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.providerType')}</p>
        <div className="flex flex-wrap gap-1">
          {Object.values(PROVIDER_TYPES).map((pt) => (
            <button
              key={pt.id}
              type="button"
              onClick={() => toggleArr('provider_types', pt.id)}
              className={`text-[11px] px-2 py-1 rounded-full border ${
                (filters.provider_types || []).includes(pt.id)
                  ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                  : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
              data-testid={`search-v2-filter-provider-type-${pt.id}`}
              title={pt.description}
            >{pt.name}</button>
          ))}
        </div>
      </div>

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.tripMode')}</p>
        <div className="flex flex-wrap gap-1" data-testid="search-v2-filter-trip-mode">
          {TRIP_MODES.map((tm) => {
            const active = filters.trip_mode === tm.id;
            return (
              <button
                key={tm.id}
                type="button"
                onClick={() => setF('trip_mode', active ? null : tm.id)}
                className={`text-[11px] px-2 py-1 rounded-full border ${
                  active
                    ? 'bg-sky-50 border-sky-300 text-sky-800'
                    : 'bg-white border-slate-200 text-slate-600 hover:border-slate-300'
                }`}
                data-testid={`search-v2-filter-trip-mode-${tm.id}`}
                aria-pressed={active}
                title={tm.description}
              >{tm.name}</button>
            );
          })}
        </div>
      </div>

      {!(taxonomy?.degraded || []).includes('languages') && (
      <div className={SECTION_STYLE} data-testid="search-v2-filter-section-languages">
        <p className={SECTION_TITLE}>{t('searchV2.filters.languages')}</p>
        <p
          className="text-[11px] text-slate-500 mb-1.5 leading-snug"
          data-testid="search-v2-filter-languages-helper"
        >
          {t('searchV2.filters.languagesNote')}
        </p>
        <SearchableMultiSelect
          label={t('searchV2.filters.languages')}
          testIdRoot="search-v2-filter-languages"
          options={(langs || []).map((l) => {
            const slug = (l && (l.slug || l.code)) || l;
            return { slug, label: (l && (l.name || l.label)) || fmt(slug) };
          })}
          selectedSlugs={filters.languages || []}
          onToggle={(s) => toggleArr('languages', s)}
          loadingText="Loading…"
          errorText="Unavailable — see error above."
          taxonomyError={taxonomyError}
        />
      </div>
      )}

      {!(taxonomy?.degraded || []).includes('accreditations') && (
      <div className={SECTION_STYLE} data-testid="search-v2-filter-section-accreditations">
        <p className={SECTION_TITLE}>{t('searchV2.filters.accreditations')}</p>
        <SearchableMultiSelect
          label={t('searchV2.filters.accreditations')}
          testIdRoot="search-v2-filter-accreditations"
          options={(accs || []).map((a) => {
            const slug = (a && (a.slug || a.code)) || a;
            const label = (a && (a.short_label || a.name || a.label)) || String(slug).toUpperCase();
            const title = (a && (a.long_label || a.name)) || slug;
            return { slug, label, title };
          })}
          selectedSlugs={filters.accreditations || []}
          onToggle={(s) => toggleArr('accreditations', s)}
          loadingText="Loading…"
          errorText="Unavailable — see error above."
          taxonomyError={taxonomyError}
        />
      </div>
      )}

      {!(taxonomy?.degraded || []).includes('specialties') && (
      <div className={SECTION_STYLE} data-testid="search-v2-filter-section-specialties">
        <p className={SECTION_TITLE}>{t('searchV2.filters.doctorSpecialty')}</p>
        <SearchableMultiSelect
          label={t('searchV2.filters.doctorSpecialty')}
          testIdRoot="search-v2-filter-specialty"
          options={(taxonomy?.specialties || []).map((s) => ({
            slug: s.slug,
            label: s.name || fmt(s.slug),
          }))}
          selectedSlugs={filters.doctor_specialty || []}
          onToggle={(s) => toggleArr('doctor_specialty', s)}
          loadingText="Loading…"
          errorText="Unavailable — see error above."
          taxonomyError={taxonomyError}
        />
      </div>
      )}

      {!(taxonomy?.degraded || []).includes('positioningTags') && (
      <div className={SECTION_STYLE} data-testid="search-v2-filter-section-positioningTags">
        <p className={SECTION_TITLE}>{t('searchV2.filters.positioningTag')}</p>
        <SearchableMultiSelect
          label={t('searchV2.filters.positioningTag')}
          testIdRoot="search-v2-filter-positioning-tag"
          options={(taxonomy?.positioningTags || []).map((t) => ({
            slug: t.slug,
            label: t.name || fmt(t.slug),
          }))}
          selectedSlugs={filters.positioning_tag || []}
          onToggle={(s) => toggleArr('positioning_tag', s)}
          loadingText="Loading…"
          errorText="Unavailable — see error above."
          taxonomyError={taxonomyError}
        />
      </div>
      )}

      {!(taxonomy?.degraded || []).includes('vipServices') && (
      <div className={SECTION_STYLE} data-testid="search-v2-filter-section-vipServices">
        <p className={SECTION_TITLE}>{t('searchV2.filters.vipService')}</p>
        <SearchableMultiSelect
          label={t('searchV2.filters.vipService')}
          testIdRoot="search-v2-filter-vip-service"
          options={(taxonomy?.vipServices || []).map((s) => ({
            slug: s.slug,
            label: s.name || fmt(s.slug),
          }))}
          selectedSlugs={filters.vip_service || []}
          onToggle={(s) => toggleArr('vip_service', s)}
          loadingText="Loading…"
          errorText="Unavailable — see error above."
          taxonomyError={taxonomyError}
        />
      </div>
      )}

      <div className={SECTION_STYLE}>
        <p className={SECTION_TITLE}>{t('searchV2.filters.shortlist')}</p>
        <label
          className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer"
          data-testid="search-v2-filter-shortlist-only-label"
        >
          <input
            type="checkbox"
            checked={filters.shortlist_only === true}
            onChange={(e) => setF('shortlist_only', e.target.checked ? true : null)}
            className="h-4 w-4 accent-rose-600"
            data-testid="search-v2-filter-shortlist-only"
          />
          {t('searchV2.filters.shortlistOnly')}
        </label>
      </div>

      {/* SRCH-V2-JURISDICTION-01 — the package filter is itself a campaign
          disclosure (it reveals that campaigns exist and lets a patient
          target them), so it is gated by the same canonical surface rule as
          the package cards and counts. */}
      {campaignsAllowed && (
        <div className={SECTION_STYLE} data-testid="search-v2-filter-ipp-section">
          <p className={SECTION_TITLE}>{t('searchV2.filters.packages')}</p>
          <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filters.has_campaign === true}
              onChange={(e) => setF('has_campaign', e.target.checked || null)}
              className="h-4 w-4 accent-emerald-600"
              data-testid="search-v2-filter-ipp"
            />
            {t('searchV2.filters.packagesOnly')}
          </label>
        </div>
      )}
      </div>
    </aside>
  );
}

// ── Active Filter Bar (bidirectional) ──────────────────────────────────
// FILTER-DATA-HARDFIX-01: every active filter axis is reflected here.
// Removing a chip mutates the same `filters` state the FilterRail reads
// from, so the rail updates in lock-step.  Clear All resets both.
function ActiveFilterBar({ filters, setFilters, taxonomy }) {
  const { t } = useTranslation();
  const trxList = taxonomy?.treatments || [];
  const accList = taxonomy?.accreditations || [];
  const langList = taxonomy?.languages || [];
  const specList = taxonomy?.specialties || [];
  const tagList = taxonomy?.positioningTags || [];
  const vipList = taxonomy?.vipServices || [];

  const _labelTrx = (slug) => {
    // SV2-OFFER-SCOPE-UNIFY-01 — resolve the catalogue name through the
    // canonical normalisation so an alias deep-link shows the same label the
    // result rows show.
    const want = normalizeTreatmentSlug(slug);
    const t = trxList.find((x) => normalizeTreatmentSlug(x.slug || x) === want);
    return (t && (t.name || t.display_name)) || fmt(slug);
  };
  const _labelAcc = (slug) => {
    const a = accList.find((x) => (x.slug || x.code || x) === slug);
    return (a && (a.short_label || a.name)) || String(slug).toUpperCase();
  };
  const _labelLang = (slug) => {
    const l = langList.find((x) => (x.slug || x.code || x) === slug);
    return (l && (l.name || l.label)) || fmt(slug);
  };
  const _labelSpec = (slug) => {
    const s = specList.find((x) => (x.slug || x) === slug);
    return (s && (s.name || s.label)) || fmt(slug);
  };
  const _labelTag = (slug) => {
    const t = tagList.find((x) => (x.slug || x) === slug);
    return (t && (t.name || t.label)) || fmt(slug);
  };
  const _labelVip = (slug) => {
    const v = vipList.find((x) => (x.slug || x) === slug);
    return (v && (v.name || v.label)) || fmt(slug);
  };

  const chips = [];
  if (filters.treatment) {
    chips.push({ key: 'treatment', label: t('searchV2.chips.treatment'), value: _labelTrx(filters.treatment), primary: true });
  }
  if (filters.country) {
    chips.push({ key: 'country', label: t('searchV2.chips.country'), value: iso2ToCountryName(filters.country) || filters.country });
  }
  if (filters.city) {
    chips.push({ key: 'city', label: t('searchV2.chips.city'), value: filters.city });
  }
  if (filters.budget_min != null) chips.push({ key: 'budget_min', label: t('searchV2.chips.min'), value: `€${filters.budget_min}` });
  if (filters.budget_max != null) chips.push({ key: 'budget_max', label: t('searchV2.chips.max'), value: `€${filters.budget_max}` });
  (filters.provider_types || []).forEach((t) => chips.push({ key: `provider_type:${t}`, label: 'Type', value: (PROVIDER_TYPES[t]?.name || fmt(t)) }));
  (filters.languages || []).forEach((l) => chips.push({ key: `lang:${l}`, label: t('searchV2.chips.language'), value: _labelLang(l) }));
  (filters.accreditations || []).forEach((a) => chips.push({ key: `acc:${a}`, label: t('searchV2.chips.accreditation'), value: _labelAcc(a) }));
  (filters.doctor_specialty || []).forEach((s) => chips.push({ key: `spec:${s}`, label: t('searchV2.chips.specialty'), value: _labelSpec(s) }));
  (filters.positioning_tag || []).forEach((s) => chips.push({ key: `tag:${s}`, label: t('searchV2.chips.positioning'), value: _labelTag(s) }));
  (filters.vip_service || []).forEach((s) => chips.push({ key: `vip:${s}`, label: 'VIP', value: _labelVip(s) }));
  if (filters.has_campaign === true) chips.push({ key: 'has_campaign', label: t('searchV2.chips.package'), value: t('searchV2.chips.packageOnly') });
  if (filters.shortlist_only === true) chips.push({ key: 'shortlist_only', label: t('searchV2.chips.view'), value: t('searchV2.chips.shortlistOnly') });
  if (filters.trip_mode) chips.push({ key: 'trip_mode', label: 'Trip Mode', value: TRIP_MODE_LABEL[filters.trip_mode] || fmt(filters.trip_mode) });

  const removeChip = (k) => {
    if (k.startsWith('provider_type:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, provider_types: (p.provider_types || []).filter((x) => x !== v) }));
    } else if (k.startsWith('lang:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, languages: (p.languages || []).filter((x) => x !== v) }));
    } else if (k.startsWith('acc:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, accreditations: (p.accreditations || []).filter((x) => x !== v) }));
    } else if (k.startsWith('spec:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, doctor_specialty: (p.doctor_specialty || []).filter((x) => x !== v) }));
    } else if (k.startsWith('tag:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, positioning_tag: (p.positioning_tag || []).filter((x) => x !== v) }));
    } else if (k.startsWith('vip:')) {
      const v = k.split(':')[1];
      setFilters((p) => ({ ...p, vip_service: (p.vip_service || []).filter((x) => x !== v) }));
    } else if (k === 'has_campaign') {
      setFilters((p) => ({ ...p, has_campaign: null }));
    } else if (k === 'shortlist_only') {
      setFilters((p) => ({ ...p, shortlist_only: null }));
    } else {
      setFilters((p) => ({ ...p, [k]: null }));
    }
  };

  return (
    <div
      className="px-4 py-3 bg-gradient-to-r from-slate-50 to-white border-b border-slate-200 flex flex-wrap items-center gap-2 min-h-[56px]"
      data-testid={chips.length === 0 ? 'search-v2-active-filters-empty' : 'search-v2-active-filters'}
    >
      <span className="text-[11px] uppercase tracking-wider font-bold text-slate-500 mr-1">
        {t('searchV2.activeFilters.heading')}
      </span>
      {chips.length === 0 ? (
        <span className="text-sm text-slate-500 italic" data-testid="search-v2-active-filters-empty-label">
          {t('searchV2.activeFilters.none')}
        </span>
      ) : (
        <>
          {chips.map((c) => (
            <span
              key={c.key}
              className={
                c.primary
                  ? 'inline-flex items-center gap-1.5 text-sm font-semibold bg-[#3BA4C5] text-white rounded-full pl-3 pr-1.5 py-1 shadow-sm'
                  : 'inline-flex items-center gap-1 text-[12px] bg-white border border-slate-300 rounded-full pl-2.5 pr-1 py-0.5'
              }
              data-testid={`search-v2-active-chip-${c.key}`}
            >
              <span className={c.primary ? 'opacity-90 text-[11px] uppercase tracking-wider font-bold' : 'font-semibold text-slate-500'}>
                {c.label}:
              </span>
              <span className={c.primary ? 'font-bold' : 'text-slate-800 font-medium'}>{c.value}</span>
              <button
                type="button"
                onClick={() => removeChip(c.key)}
                className={
                  c.primary
                    ? 'ml-1 w-5 h-5 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center text-white'
                    : 'ml-0.5 w-5 h-5 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-500'
                }
                aria-label={`Remove ${c.label} filter`}
              ><X className="h-3 w-3" /></button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => setFilters({})}
            className="ml-2 text-xs font-semibold text-slate-600 hover:text-slate-900 underline underline-offset-2"
            data-testid="search-v2-clear-all"
          >{t('searchV2.activeFilters.clearAll')}</button>
        </>
      )}
    </div>
  );
}

// ── Dense Provider Row (one row = one provider, ~60px tall) ──────────
// SRCH-V2-MEDIA-URL-01 — canonical media URL resolution.
//
// The canonical media authority returns RELATIVE paths
// (`/api/media/<id>?variant=thumbnail`). Rendering those raw only works while
// the SPA happens to share an origin with the API; seeded ABSOLUTE values
// (e.g. `https://cdn.example.com/...`) were also returned unchanged and
// failed in the browser. Resolve against the same `API` base the rest of this
// page already uses — no second media mechanism, no new endpoint.
export const resolveMediaUrl = (value) => {
  if (!value || typeof value !== 'string') return null;
  const url = value.trim();
  if (!url) return null;
  if (/^(https?:)?\/\//i.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${API}${url}`;
  return null;   // relative-without-slash is not a shape we publish
};

const _mediaFrom = (media) => {
  if (!media) return null;
  if (typeof media === 'string') return resolveMediaUrl(media);
  return resolveMediaUrl(media.thumbnail_url)
    || resolveMediaUrl(media.preview_url)
    || resolveMediaUrl(media.url)
    || null;
};

const _logoSrc = (logo) => _mediaFrom(logo);

/* TREATX-MEDIA-FALLBACK: a clinic logo URL can resolve to an object the media
   service can no longer serve (for example after a storage-namespace change).
   The <img> then renders as a broken asset on a public page, so the request is
   treated as "no logo" and the existing initials/icon fallback is shown. */
const LogoImage = ({ src, alt, className, fallback }) => {
  const [failed, setFailed] = useState(false);
  if (!src || failed) return fallback;
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} />;
};
const _initials = (name) => String(name || '?').split(/\s+/).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

// SV2-ELIGIBILITY-UX-01 — canonical-treatment rejections returned by
// `require_canonical_treatment` (the server is authoritative and fails closed).
// Used only to pick a translated sentence for the patient; never to decide
// whether a request may proceed.
const TREATMENT_REJECTION_CODES = ['TREATMENT_INVALID', 'TREATMENT_REQUIRED'];

// Treatment scope for an already server-filtered campaign list. `treatment`
// empty → provider-wide (the caller's existing contract). Jurisdiction,
// promotional-price, expiry and moderation gating all stay where they are.
export const campaignsForTreatment = (campaigns, treatment) => {
  const list = Array.isArray(campaigns) ? campaigns : [];
  if (!treatment) return list;
  const want = normalizeTreatmentSlug(treatment);
  return list.filter((c) => normalizeTreatmentSlug(c?.treatment_type) === want);
};

// SRCH-V3-CAMPAIGN-SCOPE-01 — the ONE place that decides which campaigns a
// search card may show. Two independent rules apply:
//   1. treatment scope — only campaigns explicitly associated with the searched
//      treatment. The backend already returns a treatment-strict list
//      (`resolve_treatment_aware_campaign_list`, exact normalized slug equality,
//      moderation + active-window + EUR/price guards), so this is a
//      defence-in-depth re-filter using the SAME normalisation as the server
//      (`normalize_treatment_slug`: case + underscore/hyphen).
//   2. jurisdiction — `campaigns` gates campaign EXISTENCE, `promotional_price`
//      separately gates the price/discount figures. A suppressed surface must
//      render nothing rather than a reported zero.
// Display order is the server's `created_at_utc DESC` — deterministic, and not
// re-ranked here so the card cannot invent a "best offer" claim.
// STAGE-3G-OFFER-RELEVANCE-01 — a patient must see only International Offers
// relevant to what they actually selected:
//   * treatment selected  → offers for THAT treatment only (exact canonical slug
//     equality, the same normalisation the server applies). A selected treatment
//     always takes precedence over the broader category.
//   * category only       → offers for treatments that canonically belong to the
//     category, resolved from the taxonomy ALREADY loaded by this page
//     (`/api/public/treatment-taxonomy`). No new endpoint, no extra query, and
//     no catalogue read re-introduced into the search path. Treatments the
//     catalogue does not carry have no canonical category, so they are excluded
//     rather than guessed or swept into "Other". If a selected category's
//     membership resolves to NOTHING, the patient sees NO offers — an active
//     category filter never degrades into provider-wide disclosure
//     (STAGE-3G-B2-HOTFIX-01).
//   * nothing selected    → provider-wide offers, as before.
// Relevance is a PRESENTATION scope and never affects provider eligibility.
function useTreatmentCampaigns(provider, treatment, categorySlugs = null) {
  const campaignsAllowed = useSurfaceRule('campaigns');
  const promoPriceAllowed = useSurfaceRule('promotional_price');
  return React.useMemo(() => {
    if (!campaignsAllowed) {
      return { allowed: false, promoPriceAllowed: false, count: null, list: [], scope: 'suppressed' };
    }
    const all = provider?.commercial?.active_campaigns_for_context;
    if (!Array.isArray(all)) {
      return { allowed: true, promoPriceAllowed, count: null, list: [], scope: 'unknown' };
    }
    const norm = normalizeTreatmentSlug;
    let scoped;
    let scope;
    if (treatment) {
      scoped = campaignsForTreatment(all, treatment);
      scope = 'treatment';
    } else if (categorySlugs) {
      // STAGE-3G-B2-HOTFIX-01 — an ACTIVE category filter whose canonical
      // membership resolved to nothing means "no relevant offers", NOT
      // "show everything". `null` = no category filter; `new Set()` = filter
      // active, membership unresolved. Falling back to provider-wide here
      // previously let a dental search display a BBL offer.
      scoped = all.filter((c) => categorySlugs.has(norm(c?.treatment_type)));
      scope = 'category';
    } else {
      scoped = all;
      scope = 'provider';
    }
    const list = promoPriceAllowed
      ? scoped
      : scoped.map((c) => ({
        ...c,
        campaign_price_hint: null,
        original_price_hint: null,
        discount_type: null,
        discount_value: null,
      }));
    return { allowed: true, promoPriceAllowed, count: scoped.length, list, scope };
  }, [provider, treatment, categorySlugs, campaignsAllowed, promoPriceAllowed]);
}


export function ProviderTile({ provider, selected, onSelect, treatment, treatmentName, categorySlugs, shortlisted, onShortlistToggle, isSelectedForQuote, onToggleSelectForQuote }) {
  const { t } = useTranslation();
  // SRCH-V2-ZERO-HONESTY-01 — a metric the payload does not carry is NOT a
  // zero. `(x || []).length` rendered "0" for an absent field exactly as it
  // did for a genuine, verified zero, which reads as "this provider has no
  // doctors / no accreditations". Absent data now renders an em dash. No
  // count is ever invented or defaulted.
  const verifiedCount = Array.isArray(provider.accreditations)
    ? provider.accreditations.filter(
      (a) => a?.is_verified_new === true && a?.is_self_attested !== true,
    ).length
    : null;
  const docCount = Array.isArray(provider.doctors?.public_verified_doctors)
    ? provider.doctors.public_verified_doctors.length
    : null;
  const langCount = Array.isArray(provider.languages) ? provider.languages.length : null;
  // SRCH-V3-CAMPAIGN-SCOPE-01 — treatment-scoped + jurisdiction-gated campaigns.
  // A package COUNT itself discloses campaign existence, so a suppressed surface
  // renders "unavailable" (em dash), never a reported 0.
  const campaigns = useTreatmentCampaigns(provider, treatment, categorySlugs);
  const ippCount = campaigns.count;
  const startPrice = provider.commercial?.price_min;
  const logo = _logoSrc(provider.logo) || _logoSrc(provider.logo_url);
  const treatmentLabel = treatment ? (treatmentName || fmt(treatment)) : null;

  const Metric = ({ label, value, color, tooltip, testid, widthClass = 'w-16' }) => (
    <span
      // SV2-IPAD-LAYOUT-01 — visible while the list is wide: the single-column
      // layout (md..xl) and the 3-column layout once the viewport can afford it
      // (2xl+). Hidden in the xl..2xl window, where rail + detail panel leave
      // the list ~490-580px and these columns squeezed the provider NAME to
      // zero width. The same figures remain on the provider detail panel.
      className={`hidden md:flex xl:hidden 2xl:flex flex-col items-end justify-center ${widthClass} leading-tight`}
      title={value == null ? `${tooltip} — not reported` : tooltip}
      data-testid={testid}
      data-value-state={value == null ? 'unavailable' : 'reported'}
    >
      <span className={`text-[13px] font-semibold tabular-nums ${value == null ? 'text-slate-300' : color}`}>{value == null ? '—' : value}</span>
      <span className="text-[9px] uppercase tracking-wider text-slate-400">{label}</span>
    </span>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect && onSelect(); } }}
      className={`relative w-full flex items-center gap-3 pl-3 pr-2 py-1.5 border-b border-slate-100 cursor-pointer transition-colors ${
        selected ? 'bg-[#3BA4C5]/10' : 'bg-white hover:bg-slate-50'
      }`}
      data-testid={`v2-tile-${provider.clinic_id || provider.provider_id}`}
      style={{ height: 60 }}
    >
      {/* Strong selected accent bar */}
      <span
        aria-hidden="true"
        className={`absolute left-0 top-0 bottom-0 w-1 ${selected ? 'bg-[#3BA4C5]' : 'bg-transparent'}`}
      />

      {/* Logo */}
      <div className={`flex-none w-10 h-10 rounded-md overflow-hidden border bg-slate-50 flex items-center justify-center ${selected ? 'border-[#3BA4C5]' : 'border-slate-200'}`}>
        {logo ? (
          <LogoImage
            src={logo}
            alt={provider.clinic_name}
            className="w-full h-full object-cover"
            fallback={<span className="text-[11px] font-bold text-slate-500" aria-hidden="true">{_initials(provider.clinic_name)}</span>}
          />
        ) : (
          <span className="text-[11px] font-bold text-slate-500" aria-hidden="true">{_initials(provider.clinic_name)}</span>
        )}
      </div>

      {/* Identity */}
      <div className="flex-1 min-w-0">
        <p className={`text-[13px] truncate leading-tight ${selected ? 'font-bold text-slate-900' : 'font-semibold text-slate-900'}`} data-testid={`v2-tile-name-${provider.clinic_id}`}>
          {provider.clinic_name}
        </p>
        <p className="text-[11px] truncate leading-tight text-slate-600 flex items-center gap-1.5" data-testid={`v2-tile-treatment-${provider.clinic_id}`}>
          <span className="truncate">
            {treatmentLabel || [provider.location?.city, provider.location?.country].filter(Boolean).join(', ') || '—'}
          </span>
          {/* SRCH-V3-CAMPAIGN-CARD-01 — the dense row is 60px tall, so it shows
              only the canonical short badge for the searched treatment. The
              offer itself opens in the provider panel. */}
          {campaigns.allowed && campaigns.list.length > 0 && (
            <span
              className="flex-none inline-flex items-center rounded bg-emerald-600 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wide text-white"
              data-testid={`v2-tile-campaign-${provider.clinic_id}`}
              title={campaigns.list[0].title || t('searchV2.tile.packagesTooltip')}
            >
              {campaigns.list[0].short_badge_text || t('searchV2.tile.offerBadge')}
            </span>
          )}
        </p>
      </div>

      {/* Price */}
      <div className="hidden md:flex xl:hidden 2xl:flex flex-col items-end justify-center w-24 leading-tight" title="Starting price">
        <span className="text-[13px] font-bold text-emerald-700 tabular-nums whitespace-nowrap">
          {typeof startPrice === 'number' ? `From ${eur(startPrice)}` : '—'}
        </span>
        {/* The list header already names this column; repeating it per row
            was pure noise. Cards keep their caption (they have no header). */}
      </div>

      <Metric label={t('searchV2.tile.verified')} value={verifiedCount} color="text-emerald-700" tooltip={t('searchV2.tile.verifiedTooltip')} testid={`v2-tile-acc-${provider.clinic_id}`} />
      <Metric label={t('searchV2.tile.doctors')} value={docCount} color="text-blue-700" tooltip={t('searchV2.tile.doctorsTooltip')} testid={`v2-tile-doc-${provider.clinic_id}`} />
      <Metric label={t('searchV2.tile.languages')} value={langCount} color="text-slate-700" tooltip={t('searchV2.tile.languagesTooltip')} />
      <Metric label={t('searchV2.tile.packages')} value={ippCount} color={ippCount > 0 ? 'text-rose-600' : 'text-slate-400'} tooltip={t('searchV2.tile.packagesTooltip')} testid={`v2-tile-ipp-${provider.clinic_id}`} widthClass="w-24" />

      {/* SEARCH-V2-REQUEST-QUOTE-PARITY-01 — quote-request checkbox.
          Independent from shortlist heart.  selectedClinicsV2 is the
          quote SSOT; shortlist remains save-for-later only. */}
      <label
        className="flex-none flex items-center justify-center w-7 h-9 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
        title={isSelectedForQuote ? t('searchV2.tile.selectForOffer') : t('searchV2.tile.selectForQuote')}
      >
        <input
          type="checkbox"
          checked={!!isSelectedForQuote}
          onChange={(e) => { e.stopPropagation(); onToggleSelectForQuote && onToggleSelectForQuote(); }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 accent-blue-600 cursor-pointer"
          data-testid={`v2-tile-select-quote-${provider.clinic_id}`}
          aria-label={isSelectedForQuote ? t('searchV2.tile.unselectForQuote') : t('searchV2.tile.selectForQuote')}
        />
      </label>

      {/* Shortlist */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); e.preventDefault(); onShortlistToggle && onShortlistToggle(); }}
        onMouseDown={(e) => e.stopPropagation()}
        className={`flex-none w-9 h-9 rounded-full flex items-center justify-center transition-colors border ${
          shortlisted
            ? 'bg-rose-50 border-rose-300 text-rose-600 hover:bg-rose-100'
            : 'bg-white border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-300'
        }`}
        title={shortlisted ? t('searchV2.tile.saved') : t('searchV2.tile.saveToShortlist')}
        aria-label={shortlisted ? t('searchV2.tile.savedRemove') : t('searchV2.tile.saveToShortlist')}
        data-testid={`v2-tile-shortlist-${provider.clinic_id}`}
      ><Heart className={`h-4 w-4 ${shortlisted ? 'fill-current' : ''}`} /></button>
    </div>
  );
}

// ── Provider Card (P8-A3 card presentation mode) ──────────────────────
// SRCH-V2-VIEWMODE-01 — SECOND PRESENTATION of the SAME canonical row.
// Reads the identical `provider` object and calls the identical handlers
// as ProviderTile. No separate fetch, no separate selection state, no
// derived business value. Zero-honesty rule from SRCH-V2-ZERO-HONESTY-01
// is preserved verbatim: an absent metric renders an em dash, never a 0.
export function ProviderCard({ provider, selected, onSelect, treatment, treatmentName, categorySlugs, shortlisted, onShortlistToggle, isSelectedForQuote, onToggleSelectForQuote }) {
  const { t } = useTranslation();
  const verifiedCount = Array.isArray(provider.accreditations)
    ? provider.accreditations.filter((a) => a?.is_verified_new === true && a?.is_self_attested !== true).length
    : null;
  const docCount = Array.isArray(provider.doctors?.public_verified_doctors)
    ? provider.doctors.public_verified_doctors.length
    : null;
  const langCount = Array.isArray(provider.languages) ? provider.languages.length : null;
  // SRCH-V3-CAMPAIGN-SCOPE-01 — treatment-scoped + jurisdiction-gated campaigns.
  // A package COUNT itself discloses campaign existence, so a suppressed surface
  // renders "unavailable" (em dash), never a reported 0.
  const campaigns = useTreatmentCampaigns(provider, treatment, categorySlugs);
  const ippCount = campaigns.count;
  const startPrice = provider.commercial?.price_min;
  const logo = _logoSrc(provider.logo) || _logoSrc(provider.logo_url);
  const cid = provider.clinic_id || provider.provider_id;
  const typeLabel = PROVIDER_TYPES[provider.provider_type]?.name || null;
  // SRCH-V3-CARD-TREATMENT-01 — same normalisation the server uses
  // (`normalize_treatment_slug`: case + underscore/hyphen), so a legacy-spelled
  // offered treatment still confirms the match.
  const offersSearchedTreatment = (() => {
    if (!treatment || !Array.isArray(provider.treatments)) return false;
    const norm = (s) => String(s || '').trim().toLowerCase().replace(/_/g, '-');
    const want = norm(treatment);
    return provider.treatments.some((s) => {
      const have = norm(s);
      return have === want || have === `${want}s` || `${have}s` === want;
    });
  })();

  const Cell = ({ label, value, color, testid }) => (
    <div className="flex flex-col" data-testid={testid} data-value-state={value == null ? 'unavailable' : 'reported'}>
      <span className={`text-sm font-semibold tabular-nums ${value == null ? 'text-slate-300' : color}`}>{value == null ? '—' : value}</span>
      <span className="text-[9px] uppercase tracking-wider text-slate-400">{label}</span>
    </div>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect && onSelect(); } }}
      className={`relative flex flex-col rounded-xl border bg-white p-4 cursor-pointer transition-all ${
        selected ? 'border-[#3BA4C5] ring-2 ring-[#3BA4C5]/30 shadow-sm' : 'border-slate-200 hover:border-slate-300 hover:shadow-sm'
      }`}
      data-testid={`v2-card-${cid}`}
    >
      <div className="flex items-start gap-3">
        <div className={`flex-none w-12 h-12 rounded-lg overflow-hidden border bg-slate-50 flex items-center justify-center ${selected ? 'border-[#3BA4C5]' : 'border-slate-200'}`}>
          {logo
            ? <LogoImage
                src={logo}
                alt={provider.clinic_name}
                className="w-full h-full object-cover"
                fallback={<span className="text-xs font-bold text-slate-500" aria-hidden="true">{_initials(provider.clinic_name)}</span>}
              />
            : <span className="text-xs font-bold text-slate-500" aria-hidden="true">{_initials(provider.clinic_name)}</span>}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-slate-900 leading-snug break-words" data-testid={`v2-card-name-${cid}`}>
            {provider.clinic_name}
          </p>
          <p className="text-[11px] text-slate-600 mt-0.5 flex items-center gap-1 flex-wrap">
            <MapPin className="h-3 w-3 text-slate-400 flex-none" />
            <span>{[provider.location?.city, provider.location?.country].filter(Boolean).join(', ') || '—'}</span>
            {typeLabel && <><span className="text-slate-300">·</span><span>{typeLabel}</span></>}
          </p>
          {/* SRCH-V3-CARD-TREATMENT-01 — explicit searched-treatment match.
              Rendered only when the provider's canonical offered-treatment
              projection actually contains the searched slug, so the chip is a
              statement of fact and never a category/specialty inference. */}
          {treatment && offersSearchedTreatment && (
            <span
              className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#3BA4C5]/10 px-2 py-0.5 text-[10px] font-semibold text-[#1d6f8b]"
              data-testid={`v2-card-treatment-${cid}`}
            >
              <Check className="h-3 w-3 flex-none" />
              {treatmentName || fmt(treatment)}
            </span>
          )}
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); e.preventDefault(); onShortlistToggle && onShortlistToggle(); }}
          onMouseDown={(e) => e.stopPropagation()}
          className={`flex-none w-9 h-9 rounded-full flex items-center justify-center border transition-colors ${
            shortlisted
              ? 'bg-rose-50 border-rose-300 text-rose-600 hover:bg-rose-100'
              : 'bg-white border-slate-200 text-slate-400 hover:text-rose-500 hover:border-rose-300'
          }`}
          title={shortlisted ? t('searchV2.tile.saved') : t('searchV2.tile.saveToShortlist')}
          aria-label={shortlisted ? t('searchV2.tile.savedRemove') : t('searchV2.tile.saveToShortlist')}
          data-testid={`v2-card-shortlist-${cid}`}
        ><Heart className={`h-4 w-4 ${shortlisted ? 'fill-current' : ''}`} /></button>
      </div>

      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-5 gap-2">
        <div className="flex flex-col">
          <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap">
            {typeof startPrice === 'number' ? eur(startPrice) : '—'}
          </span>
          <span className="text-[9px] uppercase tracking-wider text-slate-400">
            {treatment ? t('searchV2.tile.startingTreatment') : t('searchV2.tile.startingGlobal')}
          </span>
        </div>
        <Cell label={t('searchV2.tile.verified')} value={verifiedCount} color="text-emerald-700" testid={`v2-card-acc-${cid}`} />
        <Cell label={t('searchV2.tile.doctors')} value={docCount} color="text-blue-700" testid={`v2-card-doc-${cid}`} />
        <Cell label={t('searchV2.tile.languages')} value={langCount} color="text-slate-700" testid={`v2-card-lang-${cid}`} />
        <Cell label={t('searchV2.tile.packages')} value={ippCount} color={ippCount > 0 ? 'text-rose-600' : 'text-slate-400'} testid={`v2-card-ipp-${cid}`} />
      </div>

      {/* SRCH-V3-CAMPAIGN-CARD-01 — treatment-specific offer.
          Renders the canonical CampaignTeaser (compact) so the search card and
          the provider profile show the same offer by construction. Only
          campaigns explicitly associated with the SEARCHED treatment appear;
          additional offers stay behind the existing provider Packages tab
          rather than crowding the card. */}
      {campaigns.allowed && campaigns.list.length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100" data-testid={`v2-card-campaign-${cid}`}>
          <CampaignTeaser
            campaign={campaigns.list[0]}
            compact
            onOpen={() => onSelect && onSelect()}
            testid={`v2-card-campaign-teaser-${cid}`}
          />
          {campaigns.list.length > 1 && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onSelect && onSelect(); }}
              className="mt-1.5 text-[11px] font-medium text-emerald-700 hover:text-emerald-900 hover:underline"
              data-testid={`v2-card-campaign-more-${cid}`}
            >
              {t(campaigns.scope === 'category'
                ? 'searchV2.card.moreInCategory'
                : 'searchV2.card.morePackages', { count: campaigns.list.length - 1 })}
            </button>
          )}
        </div>
      )}


      <label
        className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-2 cursor-pointer"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <input
          type="checkbox"
          checked={!!isSelectedForQuote}
          onChange={(e) => { e.stopPropagation(); onToggleSelectForQuote && onToggleSelectForQuote(); }}
          onClick={(e) => e.stopPropagation()}
          className="h-4 w-4 accent-blue-600 cursor-pointer"
          data-testid={`v2-card-select-quote-${cid}`}
          aria-label={isSelectedForQuote ? t('searchV2.tile.unselectForQuote') : t('searchV2.tile.selectForQuote')}
        />
        <span className="text-[11px] font-medium text-slate-600">{t('searchV2.tile.selectForOffer')}</span>
      </label>
    </div>
  );
}

// ── Accreditation Viewer (large) ───────────────────────────────────────
function AccreditationViewer({ open, accs, index, onIndex, onClose }) {
  const { t } = useTranslation();
  const [zoom, setZoom] = useState(1);
  useEffect(() => { setZoom(1); }, [index]);
  if (!open || !accs || accs.length === 0) return null;
  const a = accs[index] || accs[0];
  const ev = a?.evidence || {};
  const url = resolveMediaUrl(ev.public_certificate_url) || resolveMediaUrl(ev.thumbnail_url);
  const isPdf = (ev.mime_type || '').toLowerCase().includes('pdf') || (url || '').toLowerCase().includes('.pdf');
  const total = accs.length;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent
        className="w-[92vw] max-w-[1400px] h-[88vh] p-0 bg-slate-950 border-0 flex flex-col"
        data-testid="search-v2-accreditation-viewer"
      >
        <DialogHeader className="px-4 py-3 bg-black/70 border-b border-white/10">
          <DialogTitle className="text-white text-sm flex items-center gap-2">
            <Award className="h-4 w-4 text-emerald-400" />
            {a.long_label || a.short_label || a.slug}
            <span className="text-white/50 text-xs ml-2">{index + 1} / {total}</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Accreditation certificate viewer</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-auto flex items-center justify-center bg-black/40">
          {url ? (
            isPdf ? (
              <object data={url} type="application/pdf" className="w-full h-full bg-white">
                <div className="p-8 text-center text-white">PDF preview unavailable.</div>
              </object>
            ) : (
              <img
                src={url}
                alt={a.long_label || t('searchV2.alt.certificate')}
                className="max-w-full max-h-full object-contain transition-transform"
                style={{ transform: `scale(${zoom})` }}
                data-testid="search-v2-accreditation-viewer-image"
              />
            )
          ) : (
            <div className="text-white/70 text-sm flex flex-col items-center gap-2">
              <FileText className="h-12 w-12" />
              {t('searchV2.accreditations.noCertificate')}
            </div>
          )}
        </div>
        <div className="flex items-center justify-between gap-2 px-4 py-2 bg-black/70 border-t border-white/10 text-white text-xs">
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0} className="text-white hover:bg-white/10">
              <ChevronLeft className="h-4 w-4 mr-1" />Previous
            </Button>
            <Button size="sm" variant="ghost" onClick={() => onIndex(Math.min(total - 1, index + 1))} disabled={index >= total - 1} className="text-white hover:bg-white/10">
              Next<ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.max(0.5, z - 0.25))} className="text-white hover:bg-white/10" title="Zoom out">
              <Minus className="h-3.5 w-3.5" />
            </Button>
            <span className="font-mono">{Math.round(zoom * 100)}%</span>
            <Button size="sm" variant="ghost" onClick={() => setZoom((z) => Math.min(4, z + 0.25))} className="text-white hover:bg-white/10" title="Zoom in">
              <Plus className="h-3.5 w-3.5" />
            </Button>
            {url && (
              <a href={url} target="_blank" rel="noopener noreferrer" className="ml-2 text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1">
                <Maximize2 className="h-3.5 w-3.5" />Fullscreen
              </a>
            )}
            <Button size="sm" variant="ghost" onClick={onClose} className="text-white hover:bg-white/10 ml-2">Close</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Gallery Lightbox ───────────────────────────────────────────────────
function GalleryLightbox({ open, items, index, onIndex, onClose }) {
  const { t } = useTranslation();
  if (!open || !items || items.length === 0) return null;
  const item = items[index] || items[0];
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[92vw] max-w-[1400px] h-[88vh] p-0 bg-black/95 border-0 flex flex-col" data-testid="search-v2-gallery-lightbox">
        <DialogHeader className="px-4 py-2 bg-black/70 border-b border-white/10">
          <DialogTitle className="text-white text-sm">{item.caption || `Photo ${index + 1} of ${items.length}`}</DialogTitle>
          <DialogDescription className="sr-only">Clinic photo</DialogDescription>
        </DialogHeader>
        <div className="flex-1 flex items-center justify-center bg-black/40 overflow-hidden">
          <img
            src={resolveMediaUrl(item.url) || resolveMediaUrl(item.thumbnail_url) || ''}
            alt={item.caption || t('searchV2.alt.clinicPhoto')}
            className="max-w-full max-h-full object-contain"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        </div>
        <div className="flex items-center justify-between px-4 py-2 bg-black/70 border-t border-white/10 text-white text-xs">
          <Button size="sm" variant="ghost" onClick={() => onIndex(Math.max(0, index - 1))} disabled={index === 0} className="text-white hover:bg-white/10">
            <ChevronLeft className="h-4 w-4 mr-1" />Previous
          </Button>
          <span>{index + 1} / {items.length}</span>
          <Button size="sm" variant="ghost" onClick={() => onIndex(Math.min(items.length - 1, index + 1))} disabled={index >= items.length - 1} className="text-white hover:bg-white/10">
            Next<ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ── Empty Tab micro-component (STABILIZATION-01) ──────────────────────
// Single reusable empty state used by every right-panel tab AND the
// null-provider header.  Identical icon size, identical typography,
// identical fixed min-height — eliminates per-tab visual divergence
// when sections legitimately have no data.
function EmptyTab({ icon: Icon = FileText, label, testid }) {
  return (
    <div
      className="px-4 py-6 flex flex-col items-center justify-center text-center text-slate-500 text-xs"
      style={{ minHeight: 96 }}
      data-testid={testid}
    >
      <Icon className="h-5 w-5 mb-1.5 text-slate-300" aria-hidden="true" />
      <p>{label}</p>
    </div>
  );
}

// ── Detail Panel Tabs ──────────────────────────────────────────────────
// P7-B1 — `Overview` is the canonical landing tab; `Gallery` replaces the
// former `Clinic` label so facility photography is discoverable by name.
// No tab invents data: every Overview row renders strictly what the
// canonical search projection carries, and distinguishes "not reported"
// from a reported value (SRCH-V2-ZERO-HONESTY-01).
const TABS = ['Overview', 'Packages', 'Doctors', 'Accreditations', 'Reviews', 'Gallery'];

// P7-B1 — Overview. Reads ONLY fields already present on the canonical
// `/api/search/providers` projection. A claim is rendered as verified only
// when the payload itself marks it verified.
export function OverviewTab({ provider, treatment, taxonomy }) {
  const { t } = useTranslation();
  const loc = [provider.location?.city, provider.location?.country].filter(Boolean).join(', ');
  const typeLabel = PROVIDER_TYPES[provider.provider_type]?.name || null;
  const tripLabel = TRIP_MODE_LABEL[provider.trip_mode] || null;
  const priceMin = provider.commercial?.price_min;
  const priceMax = provider.commercial?.price_max;
  const langs = provider.languages || [];
  const treatments = provider.treatments || [];
  const verifiedAccs = (provider.accreditations || []).filter(
    (a) => a?.is_verified_new === true && a?.is_self_attested !== true,
  );
  const docs = provider.doctors?.public_verified_doctors || [];
  const flags = provider.compliance_flags || {};
  const years = provider.experience?.years;
  const procedures = provider.experience?.procedures;

  const Row = ({ icon: Icon, label, children, testid }) => (
    <div className="flex items-start gap-2 py-1.5" data-testid={testid}>
      <Icon className="h-3.5 w-3.5 text-slate-400 flex-none mt-0.5" aria-hidden="true" />
      <span className="text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-24 flex-none">{label}</span>
      <span className="text-xs text-slate-800 flex-1 min-w-0 break-words">{children}</span>
    </div>
  );
  const none = <span className="text-slate-400">{t('searchV2.overview.notReported')}</span>;

  return (
    <div className="p-3 space-y-4" data-testid="v2-tab-overview">
      <section className="rounded-lg border border-slate-200 divide-y divide-slate-100 px-3">
        <Row icon={MapPin} label={t('searchV2.overview.location')} testid="v2-overview-location">{loc || none}</Row>
        <Row icon={Building2} label={t('searchV2.overview.type')} testid="v2-overview-type">{typeLabel || none}</Row>
        <Row icon={Plane} label={t('searchV2.overview.tripMode')} testid="v2-overview-trip-mode">{tripLabel || none}</Row>
        <Row icon={Tag} label={treatment ? t('searchV2.overview.priceTreatment') : t('searchV2.overview.price')} testid="v2-overview-price">
          {typeof priceMin === 'number'
            ? `${eur(priceMin)}${typeof priceMax === 'number' && priceMax !== priceMin ? ` – ${eur(priceMax)}` : ''}`
            : none}
        </Row>
        <Row icon={Globe} label={t('searchV2.overview.languages')} testid="v2-overview-languages">
          {langs.length > 0 ? langs.join(' · ') : none}
        </Row>
      </section>

      <section data-testid="v2-overview-verified">
        <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1.5">{t('searchV2.overview.verifiedHeading')}</h4>
        <div className="grid grid-cols-2 gap-1.5">
          {[
            { key: 'accs', label: t('searchV2.overview.accreditations'), value: verifiedAccs.length, ok: verifiedAccs.length > 0 },
            { key: 'docs', label: t('searchV2.overview.verifiedDoctors'), value: docs.length, ok: docs.length > 0 },
            { key: 'iso9001', label: t('searchV2.overview.iso9001'), value: flags.has_iso9001 === true ? t('searchV2.overview.yes') : t('searchV2.overview.notVerified'), ok: flags.has_iso9001 === true },
            { key: 'license', label: t('searchV2.overview.licence'), value: flags.has_medical_license === true ? t('searchV2.overview.yes') : t('searchV2.overview.notVerified'), ok: flags.has_medical_license === true },
          ].map((f) => (
            <div
              key={f.key}
              className={`flex items-center gap-1.5 rounded-md border px-2 py-1.5 ${f.ok ? 'border-emerald-200 bg-emerald-50' : 'border-slate-200 bg-white'}`}
              data-testid={`v2-overview-flag-${f.key}`}
              data-value-state={f.ok ? 'verified' : 'unverified'}
            >
              {f.ok
                ? <BadgeCheck className="h-3.5 w-3.5 text-emerald-600 flex-none" aria-hidden="true" />
                : <Minus className="h-3.5 w-3.5 text-slate-300 flex-none" aria-hidden="true" />}
              <span className="min-w-0">
                <span className={`block text-xs font-semibold tabular-nums ${f.ok ? 'text-emerald-800' : 'text-slate-400'}`}>{f.value}</span>
                <span className="block text-[9px] uppercase tracking-wider text-slate-500 truncate">{f.label}</span>
              </span>
            </div>
          ))}
        </div>
        {(years?.verified === true || procedures?.verified === true) && (
          <p className="text-[11px] text-slate-600 mt-1.5" data-testid="v2-overview-experience">
            {years?.verified === true && t('searchV2.overview.experienceYears', { count: years.value })}
            {years?.verified === true && procedures?.verified === true && ' · '}
            {procedures?.verified === true && t('searchV2.overview.experienceProcedures', { count: procedures.value })}
          </p>
        )}
      </section>

      <section data-testid="v2-overview-treatments">
        <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1.5">
          {t('searchV2.overview.treatments')} <span className="text-slate-400 font-normal">({treatments.length})</span>
        </h4>
        {treatments.length === 0 ? (
          <p className="text-xs text-slate-500">{t('searchV2.overview.notReported')}</p>
        ) : (
          <div className="flex flex-wrap gap-1">
            {treatments.map((t) => (
              <span
                key={t}
                className={`text-[10px] px-2 py-0.5 rounded-full border ${
                  treatment && String(t).toLowerCase() === String(treatment).toLowerCase()
                    ? 'bg-[#3BA4C5]/10 border-[#3BA4C5] text-slate-900 font-semibold'
                    : 'bg-slate-50 border-slate-200 text-slate-600'
                }`}
              >{treatmentNameOf(taxonomy, t)}</span>
            ))}
          </div>
        )}
      </section>

      {/* P5-E — VIP services and positioning tags were ALREADY present on the
          canonical public search projection (`compliance_summary_service`
          §12b, sourced from `clinic_profiles`, order-preserving + deduped).
          No projection change was needed. VIP services are additionally
          published by the canonical public clinic-profile endpoint, so they
          are an approved public value. Positioning tags are PROVIDER-DECLARED
          marketing positioning, not a TreatX verification, and are labelled
          as such — they are deliberately NOT presented as a trust signal. */}
      {(provider.vip_services || []).length > 0 && (
        <section data-testid="v2-overview-vip-services">
          <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1.5">
            {t('searchV2.overview.services')}
          </h4>
          <div className="flex flex-wrap gap-1">
            {(provider.vip_services || []).map((v) => (
              <span key={v} className="text-[10px] px-2 py-0.5 rounded-full border border-slate-200 bg-slate-50 text-slate-700">
                {fmt(v)}
              </span>
            ))}
          </div>
        </section>
      )}

      {(provider.positioning_tags || []).length > 0 && (
        <section data-testid="v2-overview-positioning">
          <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-0.5">
            {t('searchV2.overview.positioning')}
          </h4>
          <p className="text-[10px] text-slate-500 mb-1.5">{t('searchV2.overview.positioningNote')}</p>
          <div className="flex flex-wrap gap-1">
            {(provider.positioning_tags || []).map((v) => (
              <span key={v} className="text-[10px] px-2 py-0.5 rounded-full border border-dashed border-slate-300 bg-white text-slate-600">
                {fmt(v)}
              </span>
            ))}
          </div>
        </section>
      )}

      {provider.experience?.about && (
        <section data-testid="v2-overview-about">
          <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1">{t('searchV2.overview.about')}</h4>
          <p className="text-xs text-slate-700 leading-relaxed">{provider.experience.about}</p>
        </section>
      )}
    </div>
  );
}

export function PackagesTab({ provider, treatment, treatmentName, onRequest }) {
  const { t } = useTranslation();
  // SRCH-V2-JURISDICTION-01 — campaign / package presentation is a REGULATED
  // capability. The canonical authority is the server-resolved surface rule
  // matrix (`services/surface_resolver.py` → `/api/surface/context`), consumed
  // through the existing `useSurfaceRule` hook. No host sniffing, no IP
  // detection, no inference from UI language, and no second resolver: the hook
  // already returns `false` while unresolved (fail-closed) and `true` for
  // GLOBAL. `promotional_price` separately gates the campaign price hint.
  const campaignsAllowed = useSurfaceRule('campaigns');
  const promoPriceAllowed = useSurfaceRule('promotional_price');
  const all = campaignsAllowed ? (provider.commercial?.active_campaigns_for_context || []) : [];
  // SV2-OFFER-SCOPE-UNIFY-01 — same normalisation as the card badge.
  const filtered = campaignsForTreatment(all, treatment);
  if (filtered.length === 0) {
    return (
      <EmptyTab
        icon={Tag}
        label={!campaignsAllowed
          ? t('searchV2.packages.suppressedBySurface')
          : (treatment
            ? t('searchV2.packages.noneForTreatment', { treatment: treatmentName || fmt(treatment) })
            : t('searchV2.packages.none'))}
        testid="v2-tab-packages-empty"
      />
    );
  }
  return (
    <div className="space-y-3 p-3" data-testid="v2-tab-packages">
      {filtered.map((c) => (
        <article key={c.campaign_id} className="rounded-lg border border-emerald-200 bg-white overflow-hidden" data-testid={`v2-package-${c.campaign_id}`}>
          {c.hero_image_url && (
            <div className="aspect-[16/7] bg-slate-100 overflow-hidden">
              <img src={c.hero_image_url} alt={c.title} className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-3 space-y-2">
            <div className="flex items-start justify-between gap-2">
              <h4 className="font-semibold text-slate-900 text-sm leading-tight">{c.title || t('internationalOffer.untitled')}</h4>
              <span className="font-bold text-emerald-700 text-sm whitespace-nowrap">
                {promoPriceAllowed && typeof c.campaign_price_hint === 'number' ? eur(c.campaign_price_hint) : '—'}
              </span>
            </div>
            {c.description && <p className="text-xs text-slate-600 leading-relaxed line-clamp-3">{c.description}</p>}
            <Button size="sm" onClick={() => onRequest && onRequest(c)} className="bg-emerald-600 hover:bg-emerald-700 text-white w-full">
              {t('searchV2.packages.requestOffer')}
            </Button>
          </div>
        </article>
      ))}
    </div>
  );
}

function DoctorsTab({ provider, treatment, treatmentName, onOpenDoctor, onPreferDoctor, preferredDoctorId }) {
  const { t } = useTranslation();
  const all = provider.doctors?.public_verified_doctors || [];
  const filtered = treatment ? all.filter((d) => {
    const tx = normalizeTreatmentSlug(treatment);
    return (d.specialties || []).some((s) => {
      const ss = String(s).toLowerCase();
      return ss === tx || ss.includes(tx.split('-')[0]) || tx.includes(ss);
    });
  }) : all;
  if (filtered.length === 0) {
    return (
      <EmptyTab
        icon={Stethoscope}
        label={treatment
          ? t('searchV2.doctors.noneForTreatment', { treatment: treatmentName || fmt(treatment) })
          : t('searchV2.doctors.none')}
        testid="v2-tab-doctors-empty"
      />
    );
  }
  return (
    <div className="space-y-2 p-3" data-testid="v2-tab-doctors">
      {filtered.map((d) => (
        <div key={d.doctor_id} className="flex items-stretch gap-2">
        <button
          type="button"
          onClick={() => onOpenDoctor(d)}
          className="w-full flex gap-3 p-3 rounded-lg border border-slate-200 bg-white text-left hover:border-[#3BA4C5] hover:shadow-sm transition-all"
          data-testid={`v2-doctor-card-${d.doctor_id}`}
        >
          <div className="flex-none w-12 h-12 rounded-full overflow-hidden border border-slate-200 bg-slate-50">
            {d.profile_photo_url ? (
              <img src={d.profile_photo_url} alt={d.full_name} className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300"><Stethoscope className="h-5 w-5" /></div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-medium text-slate-900 text-sm truncate">{d.full_name}</p>
            <p className="text-[11px] text-slate-600 truncate">
              {(d.specialties || [])[0] ? fmt(d.specialties[0]) : '—'}
              {d.years_of_experience > 0 && ` · ${d.years_of_experience}+ yrs`}
            </p>
            {(d.languages || []).length > 0 && (
              <p className="text-[10px] text-slate-500 mt-0.5 truncate">
                <Globe className="h-2.5 w-2.5 inline mr-0.5" />
                {(d.languages || []).slice(0, 4).join(' · ')}
              </p>
            )}
          </div>
        </button>
        {onPreferDoctor && (
          <button
            type="button"
            onClick={() => onPreferDoctor(d)}
            aria-pressed={preferredDoctorId === d.doctor_id}
            title={t('searchV2.doctors.preferenceOnly')}
            className={`flex-none px-2 rounded-lg border text-[11px] font-medium transition-colors ${
              preferredDoctorId === d.doctor_id
                ? 'border-[#3BA4C5] bg-[#EAF6FA] text-[#1F7A96]'
                : 'border-slate-200 bg-white text-slate-600 hover:border-[#3BA4C5]'
            }`}
            data-testid={`v2-doctor-prefer-${d.doctor_id}`}
          >
            {preferredDoctorId === d.doctor_id
              ? t('searchV2.doctors.preferred')
              : t('searchV2.doctors.prefer')}
          </button>
        )}
        </div>
      ))}
    </div>
  );
}

function AccreditationsTab({ provider, onOpenViewer }) {
  const { t } = useTranslation();
  const accs = (provider.accreditations || []).filter(
    (a) => a?.is_verified_new === true && a?.is_self_attested !== true,
  );
  if (accs.length === 0) {
    return (
      <EmptyTab
        icon={Award}
        label={t('searchV2.accreditations.none')}
        testid="v2-tab-accs-empty"
      />
    );
  }
  return (
    <div className="grid gap-2 p-3 [grid-template-columns:repeat(auto-fill,minmax(170px,1fr))]" data-testid="v2-tab-accs">
      {accs.map((a, i) => (
        <button
          type="button"
          key={a.slug}
          onClick={() => onOpenViewer(accs, i)}
          className="flex flex-col items-stretch rounded-lg border border-emerald-200 bg-white overflow-hidden hover:border-emerald-400 hover:shadow-sm transition-all"
          data-testid={`v2-acc-card-${a.slug}`}
        >
          <div className="aspect-[4/3] bg-slate-50">
            {a?.evidence?.thumbnail_url ? (
              <img
                src={resolveMediaUrl(a.evidence.thumbnail_url) || ''}
                alt={a.long_label}
                className="w-full h-full object-contain"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-slate-300"><Award className="h-8 w-8" /></div>
            )}
          </div>
          <div className="p-2">
            <p className="text-[11px] font-semibold text-slate-900 truncate">{a.long_label || a.short_label}</p>
            <p className="text-[10px] text-emerald-700 uppercase tracking-wider truncate">{a.short_label || a.slug}</p>
          </div>
        </button>
      ))}
    </div>
  );
}

function ReviewsTab() {
  const { t } = useTranslation();
  return (
    <EmptyTab
      icon={Star}
      label={t('searchV2.reviews.pending')}
      testid="v2-tab-reviews"
    />
  );
}

// P7-B1 — Gallery (formerly the `Clinic` tab). Facility photography only;
// the former About / location / languages block now lives in Overview so
// each tab has exactly one job. Media still flows through the canonical
// approved+published projection — no moderation bypass.
export function GalleryTab({ provider, onOpenGallery }) {
  const { t } = useTranslation();
  const gallery = provider.gallery || [];
  return (
    <div className="p-3" data-testid="v2-tab-gallery">
      <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1.5 flex items-center gap-1">
        <ImageIcon className="h-3 w-3" />{t('searchV2.gallery.heading')}
        <span className="text-slate-400 font-normal">({gallery.length})</span>
      </h4>
      {gallery.length === 0 ? (
        <p className="text-xs text-slate-500">{t('searchV2.gallery.none')}</p>
      ) : (
        <div
          className="grid gap-2 [grid-template-columns:repeat(auto-fill,minmax(96px,1fr))]"
          data-testid="v2-clinic-gallery-grid"
        >
          {gallery.map((g, i) => (
            <button
              key={g.media_id || i}
              type="button"
              onClick={() => onOpenGallery(gallery, i)}
              className="aspect-square rounded-md overflow-hidden border border-slate-200 hover:border-[#3BA4C5] hover:shadow-sm transition-all"
              data-testid={`v2-clinic-gallery-${i}`}
            >
              <img
                src={_mediaFrom(g) || ''}
                alt={g.caption || `Photo ${i + 1}`}
                className="w-full h-full object-cover"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Sticky Provider Detail Panel ──────────────────────────────────────
// STABILIZATION-01: panel is ALWAYS mounted on desktop.  When provider
// is null, a deterministic empty skeleton renders (same header height,
// same tab bar, same body container, all tab counts = 0).  This removes
// the prior "section appears/disappears" perception entirely.
export function ProviderDetailPanel({ provider, treatment, taxonomy, onOpenDoctor, onPreferDoctor, preferredDoctorId, onOpenAcc, onOpenGallery, onRequest, onClose, expanded, onToggleExpanded }) {
  const { t } = useTranslation();
  const campaignsAllowed = useSurfaceRule('campaigns');
  const [tab, setTab] = useState('Overview');
  // STABILIZATION-01 amendment 1: tab state is provider-specific.
  // Resetting on every provider change is the canonical expected
  // behaviour and is explicitly preserved. P7-B1 moves the landing tab
  // from `Packages` to `Overview`.
  useEffect(() => { setTab('Overview'); }, [provider?.clinic_id]);

  // Compute per-tab counts for the always-visible tab badges.
  const counts = useMemo(() => {
    if (!provider) return { Packages: 0, Doctors: 0, Accreditations: 0, Reviews: 0, Gallery: 0 };
    const allPkg = campaignsAllowed ? (provider.commercial?.active_campaigns_for_context || []) : [];
    const pkg = campaignsForTreatment(allPkg, treatment);
    const allDoc = provider.doctors?.public_verified_doctors || [];
    const doc = treatment ? allDoc.filter((d) => {
      // SV2-OFFER-SCOPE-UNIFY-01 — same canonical spelling as every other
      // treatment comparison; the specialty heuristic itself is unchanged.
      const tx = normalizeTreatmentSlug(treatment);
      return (d.specialties || []).some((s) => {
        const ss = String(s).toLowerCase();
        return ss === tx || ss.includes(tx.split('-')[0]) || tx.includes(ss);
      });
    }) : allDoc;
    const acc = (provider.accreditations || []).filter((a) => a?.is_verified_new === true && a?.is_self_attested !== true);
    const gal = provider.gallery || [];
    return { Packages: pkg.length, Doctors: doc.length, Accreditations: acc.length, Reviews: 0, Gallery: gal.length };
  }, [provider, treatment, campaignsAllowed]);

  const headerName = provider ? provider.clinic_name : t('searchV2.detail.noProvider');
  const headerLoc = provider
    ? ([provider.location?.city, provider.location?.country].filter(Boolean).join(', ') || '—')
    : 'Empty result set';
  const headerLogo = provider ? (_logoSrc(provider.logo) || _logoSrc(provider.logo_url)) : null;

  return (
    <aside className={`${expanded ? 'flex-1 min-w-0' : 'w-full xl:w-[460px] xl:flex-none'} border-l border-slate-200 bg-white flex flex-col min-h-0 xl:shadow-[-1px_0_0_0_rgba(15,23,42,0.04)]`} data-testid="search-v2-detail-panel">
      <header className="px-4 py-2.5 border-b border-slate-200 flex items-start gap-3 flex-none">
        <div className="flex-none w-12 h-12 rounded-md overflow-hidden border border-slate-200 bg-slate-50 flex items-center justify-center">
          {headerLogo ? (
            <LogoImage
              src={headerLogo}
              alt={provider.clinic_name}
              className="w-full h-full object-cover"
              fallback={<Building2 className="h-5 w-5 text-slate-300" aria-hidden="true" />}
            />
          ) : (
            <Building2 className="h-5 w-5 text-slate-300" aria-hidden="true" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">{t('searchV2.panels.details')}</p>
          <p className="font-semibold text-slate-900 text-sm truncate" data-testid="search-v2-detail-name">{headerName}</p>
          <p className="text-[11px] text-slate-500 flex items-center gap-1 truncate">
            <MapPin className="h-3 w-3 flex-none" />{headerLoc}
          </p>
        </div>
        {/* P8-A2 — THE single expand/collapse control. Repairs the former
            28px unlabelled rail, which rendered as a bare chevron on a
            white strip between two white panels and was reported invisible.
            Visible in BOTH states, labelled, keyboard reachable, and never
            overlapping content because it sits inside the panel header. */}
        {onToggleExpanded && (
          <button
            type="button"
            onClick={onToggleExpanded}
            aria-pressed={!!expanded}
            aria-label={expanded ? t('searchV2.detail.collapseAria') : t('searchV2.detail.expandAria')}
            title={expanded ? t('searchV2.detail.collapse') : t('searchV2.detail.expand')}
            data-testid="search-v2-center-collapse-toggle"
            className="hidden lg:inline-flex flex-none items-center gap-1.5 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700 transition-colors hover:border-[#3BA4C5] hover:text-[#3BA4C5] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3BA4C5]"
          >
            {expanded
              ? <><Minimize2 className="h-3.5 w-3.5" aria-hidden="true" />{t('searchV2.detail.collapse')}</>
              : <><Maximize2 className="h-3.5 w-3.5" aria-hidden="true" />{t('searchV2.detail.expand')}</>}
          </button>
        )}
        {/* STABILIZATION-01 amendment 3: X close affordance is only
            rendered when an explicit onClose is supplied (mobile
            overlay).  Desktop mount omits the prop so the panel is
            permanent. */}
        {onClose && (
          <button type="button" onClick={onClose} className="p-1 text-slate-400 hover:text-slate-700" title={t('searchV2.detail.close')} data-testid="search-v2-detail-close"><X className="h-4 w-4" /></button>
        )}
      </header>
      {/* P8-A1 — the tab strip WRAPS rather than scrolling or truncating.
          Six labels with their counts exceed 460px, and a horizontally
          scrolled strip hid `Gallery` off-screen — the exact opposite of
          the P7-B1 discoverability requirement. */}
      <nav className="flex flex-wrap border-b border-slate-200 bg-slate-50 flex-none" data-testid="search-v2-detail-tabs">
        {TABS.map((tabName) => (
          <button
            key={tabName}
            type="button"
            onClick={() => setTab(tabName)}
            disabled={!provider}
            className={`flex-none whitespace-nowrap px-3 text-[11px] font-semibold py-2 transition-colors ${
              tab === tabName ? 'text-[#3BA4C5] border-b-2 border-[#3BA4C5] bg-white' : 'text-slate-500 hover:text-slate-700'
            } ${!provider ? 'opacity-60 cursor-not-allowed' : ''}`}
            data-testid={`search-v2-detail-tab-${tabName.toLowerCase()}`}
          >
            {t(`searchV2.tabs.${tabName}`)}{counts[tabName] !== undefined && <span className="text-slate-400 tabular-nums"> ({counts[tabName]})</span>}
          </button>
        ))}
      </nav>
      <div className="flex-1 overflow-y-auto min-h-0" data-testid="search-v2-detail-body">
        {/* P8-A2 — an expanded panel is a WIDER READING AREA, not an
            infinitely wide line length. */}
        <div className={expanded ? 'mx-auto w-full max-w-5xl' : ''}>
        {!provider ? (
          <EmptyTab
            icon={ArrowLeft}
            label={t('searchV2.detail.noMatch')}
            testid="search-v2-detail-empty-body"
          />
        ) : (
          <>
            {tab === 'Overview' && <OverviewTab provider={provider} treatment={treatment} taxonomy={taxonomy} />}
            {tab === 'Packages' && <PackagesTab provider={provider} treatment={treatment} treatmentName={treatmentNameOf(taxonomy, treatment)} onRequest={onRequest} />}
            {tab === 'Doctors' && <DoctorsTab provider={provider} treatment={treatment} treatmentName={treatmentNameOf(taxonomy, treatment)} onOpenDoctor={onOpenDoctor} onPreferDoctor={onPreferDoctor} preferredDoctorId={preferredDoctorId} />}
            {tab === 'Accreditations' && <AccreditationsTab provider={provider} onOpenViewer={onOpenAcc} />}
            {tab === 'Reviews' && <ReviewsTab />}
            {tab === 'Gallery' && <GalleryTab provider={provider} onOpenGallery={onOpenGallery} />}
          </>
        )}
        </div>
      </div>
    </aside>
  );
}

// ── Treatment Details (P7-B2) ─────────────────────────────────────────
// Patient-facing, READ-ONLY view of the canonical treatment catalog entry.
// Source: `GET /api/public/treatments/{slug}` — the existing public
// catalog authority (routes/treatment_content_routes.py), which serves
// only `status: published` rows. No new endpoint, no treatment CRUD, no
// second treatment authority, and nothing clinic-private is read: this
// endpoint carries no clinic-scoped data at all. Every section renders
// only if the canonical payload actually carries it.
export function TreatmentDetailsModal({ open, slug, provider, onClose }) {
  const { t } = useTranslation();
  const [general, setGeneral] = useState({ loading: false, data: null, error: false });
  const [clinic, setClinic] = useState({ loading: false, data: null, error: false });
  const clinicId = provider?.clinic_id || provider?.provider_id || null;

  // General catalogue entry — canonical published master content.
  useEffect(() => {
    if (!open || !slug) return undefined;
    let cancelled = false;
    setGeneral({ loading: true, data: null, error: false });
    (async () => {
      try {
        const r = await axios.get(`${API}/api/public/treatments/${encodeURIComponent(slug)}`);
        if (!cancelled) setGeneral({ loading: false, data: r.data?.treatment || null, error: !r.data?.treatment });
      } catch (_) {
        if (!cancelled) setGeneral({ loading: false, data: null, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [open, slug]);

  // SRCH-V2-PROVIDER-TREATMENT-01 — provider-specific published content from
  // the canonical public clinic-profile projection. The `treatment_showcase`
  // overlay is built from the `clinic_treatments` SSOT under the SAME
  // catalogue-membership and media-moderation predicates the profile surface
  // already enforces. Re-fetched per provider so Provider A can never render
  // Provider B's copy.
  useEffect(() => {
    if (!open || !slug || !clinicId) { setClinic({ loading: false, data: null, error: false }); return undefined; }
    let cancelled = false;
    setClinic({ loading: true, data: null, error: false });
    (async () => {
      try {
        const r = await axios.get(
          `${API}/api/public/clinics/${encodeURIComponent(clinicId)}/profile`,
          { params: { treatment_type: slug } },
        );
        const showcase = r.data?.treatment_showcase || {};
        if (!cancelled) setClinic({ loading: false, data: showcase[slug] || null, error: false });
      } catch (_) {
        if (!cancelled) setClinic({ loading: false, data: null, error: true });
      }
    })();
    return () => { cancelled = true; };
  }, [open, slug, clinicId]);

  const g = general.data;
  const desc = g?.description || {};
  const c = clinic.data;
  const hasClinicCopy = !!c && [
    c.short_description, c.detailed_description, c.indications,
    c.contraindications, c.preparation_info, c.recovery_info,
  ].some((v) => !!v);

  const Para = ({ title, value, testid }) => (value ? (
    <section data-testid={testid}>
      <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1">{title}</h4>
      <p className="text-sm text-slate-700 leading-relaxed whitespace-pre-line">{value}</p>
    </section>
  ) : null);
  const List = ({ title, items, testid }) => (
    (Array.isArray(items) && items.length > 0) ? (
      <section data-testid={testid}>
        <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1">{title}</h4>
        <ul className="list-disc pl-4 space-y-0.5 text-sm text-slate-700">
          {items.map((x, i) => <li key={i}>{x}</li>)}
        </ul>
      </section>
    ) : null
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl w-[92vw] max-h-[85vh] overflow-y-auto" data-testid="search-v2-treatment-details-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Info className="h-4 w-4" />{g?.name || c?.headline || fmt(slug || '')}
          </DialogTitle>
          <DialogDescription>
            {t('searchV2.treatmentModal.disclaimer')}
          </DialogDescription>
        </DialogHeader>

        {(general.loading || clinic.loading) && (
          <div className="flex items-center gap-2 py-8 justify-center text-sm text-slate-500" data-testid="search-v2-treatment-details-loading">
            <Loader2 className="h-4 w-4 animate-spin" />{t('searchV2.treatmentModal.loading')}
          </div>
        )}

        {!general.loading && !clinic.loading && (
          <div className="space-y-5" data-testid="search-v2-treatment-details-body">
            {/* ── Provider-specific published information ────────────── */}
            {clinicId && (
              <section
                className="rounded-xl border-2 border-[#3BA4C5]/40 bg-[#3BA4C5]/5 p-3 space-y-3"
                data-testid="search-v2-treatment-clinic-section"
              >
                <header className="flex items-start gap-2">
                  <Building2 className="h-4 w-4 text-[#3BA4C5] flex-none mt-0.5" aria-hidden="true" />
                  <div className="min-w-0">
                    <h3 className="text-sm font-semibold text-slate-900" data-testid="search-v2-treatment-clinic-heading">
                      {t('searchV2.treatmentModal.clinicSection')}
                    </h3>
                    <p className="text-[11px] text-slate-600 truncate">{provider?.clinic_name}</p>
                  </div>
                </header>

                {!hasClinicCopy ? (
                  <p className="text-xs text-slate-600" data-testid="search-v2-treatment-clinic-empty">
                    {t('searchV2.treatmentModal.clinicEmpty')}
                  </p>
                ) : (
                  <div className="space-y-3">
                    <Para title={t('searchV2.treatmentModal.summary')} value={c.short_description} testid="search-v2-treatment-clinic-short" />
                    <Para title={t('searchV2.treatmentModal.details')} value={c.detailed_description} testid="search-v2-treatment-clinic-detailed" />
                    <Para title={t('searchV2.treatmentModal.indications')} value={c.indications} testid="search-v2-treatment-clinic-indications" />
                    <Para title={t('searchV2.treatmentModal.contraindications')} value={c.contraindications} testid="search-v2-treatment-clinic-contraindications" />
                    <Para title={t('searchV2.treatmentModal.preparation')} value={c.preparation_info} testid="search-v2-treatment-clinic-preparation" />
                    <Para title={t('searchV2.treatmentModal.recovery')} value={c.recovery_info} testid="search-v2-treatment-clinic-recovery" />
                    <List title={t('searchV2.treatmentModal.inclusions')} items={c.inclusions} testid="search-v2-treatment-clinic-inclusions" />
                  </div>
                )}

                {typeof c?.price_min === 'number' && (
                  <p className="text-xs text-slate-800 border-t border-[#3BA4C5]/30 pt-2" data-testid="search-v2-treatment-clinic-price">
                    <span className="font-semibold">{t('searchV2.treatmentModal.clinicPrice')}: </span>
                    {eur(c.price_min)}
                    {typeof c.price_max === 'number' && c.price_max !== c.price_min ? ` – ${eur(c.price_max)}` : ''}
                  </p>
                )}
              </section>
            )}

            {/* ── General catalogue information ──────────────────────── */}
            {general.error && (
              <p className="py-6 text-center text-sm text-slate-500" data-testid="search-v2-treatment-details-error">
                {t('searchV2.treatmentModal.generalUnavailable')}
              </p>
            )}
            {g && (
              <section className="space-y-4" data-testid="search-v2-treatment-general-section">
                <header>
                  <h3 className="text-sm font-semibold text-slate-900" data-testid="search-v2-treatment-general-heading">
                    {t('searchV2.treatmentModal.generalSection')}
                  </h3>
                  <p className="text-[11px] text-slate-500">{t('searchV2.treatmentModal.generalNote')}</p>
                </header>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    { label: t('searchV2.treatmentModal.procedure'), value: g.procedureDuration, testid: 'duration' },
                    { label: t('searchV2.treatmentModal.recovery'), value: g.recoveryTime, testid: 'recovery' },
                    { label: t('searchV2.treatmentModal.stay'), value: g.stayDuration, testid: 'stay' },
                    {
                      label: t('searchV2.treatmentModal.generalEstimate'),
                      value: (typeof g.priceRange?.min === 'number' && typeof g.priceRange?.max === 'number')
                        ? `${g.priceRange.currency || 'EUR'} ${g.priceRange.min.toLocaleString('en-US')}–${g.priceRange.max.toLocaleString('en-US')}`
                        : null,
                      testid: 'price',
                    },
                  ].map((f) => (
                    <div
                      key={f.testid}
                      className="rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2"
                      data-testid={`search-v2-treatment-fact-${f.testid}`}
                      data-value-state={f.value ? 'reported' : 'unavailable'}
                    >
                      <span className="block text-[9px] uppercase tracking-wider text-slate-500 font-semibold">{f.label}</span>
                      <span className={`block text-xs font-semibold ${f.value ? 'text-slate-900' : 'text-slate-400'}`}>
                        {f.value || t('searchV2.treatmentModal.notPublished')}
                      </span>
                    </div>
                  ))}
                </div>
                {/* A general catalogue estimate must never be mistaken for the
                    selected clinic's own offer. */}
                <p className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-2 py-1" data-testid="search-v2-treatment-estimate-note">
                  {t('searchV2.treatmentModal.estimateNote')}
                </p>

                <Para title={t('searchV2.treatmentModal.overview')} value={desc.overview} testid="search-v2-treatment-overview" />
                <List title={t('searchV2.treatmentModal.whoFor')} items={g.whoIsThisFor} testid="search-v2-treatment-who" />
                <List title={t('searchV2.treatmentModal.benefits')} items={desc.benefits} testid="search-v2-treatment-benefits" />
                <List title={t('searchV2.treatmentModal.risks')} items={desc.risks} testid="search-v2-treatment-risks" />
                <Para title={t('searchV2.treatmentModal.expectations')} value={desc.expectations} testid="search-v2-treatment-expectations" />
                <List title={t('searchV2.treatmentModal.whyAbroad')} items={g.whyAbroad} testid="search-v2-treatment-why-abroad" />
                {Array.isArray(g.faqs) && g.faqs.length > 0 && (
                  <section data-testid="search-v2-treatment-faqs">
                    <h4 className="font-semibold text-slate-800 text-xs uppercase tracking-wider mb-1">{t('searchV2.treatmentModal.faq')}</h4>
                    <div className="space-y-2">
                      {g.faqs.map((f, i) => (
                        <div key={i} className="rounded-lg border border-slate-200 p-2.5">
                          <p className="text-xs font-semibold text-slate-900">{f.question}</p>
                          <p className="text-xs text-slate-700 mt-0.5 leading-relaxed">{f.answer}</p>
                        </div>
                      ))}
                    </div>
                  </section>
                )}
              </section>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ── Compare Modal ─────────────────────────────────────────────────────
export function CompareModal({ open, onClose, providers, treatment, treatmentName }) {
  const { t } = useTranslation();
  const campaignsAllowed = useSurfaceRule('campaigns');
  const promoPriceAllowed = useSurfaceRule('promotional_price');
  if (!open || providers.length < 2) return null;
  const row = (label, render) => (
    <tr className="border-b border-slate-100">
      <td className="py-2 pr-3 text-[11px] uppercase tracking-wider text-slate-500 font-semibold w-32 align-top">{label}</td>
      {providers.map((p) => (
        <td key={p.clinic_id} className="py-2 px-2 text-xs text-slate-800 align-top" data-testid={`v2-compare-${label.toLowerCase().replace(/ /g, '-')}-${p.clinic_id}`}>
          {render(p)}
        </td>
      ))}
    </tr>
  );
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-5xl w-[92vw] max-h-[90vh] overflow-y-auto" data-testid="search-v2-compare-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="h-4 w-4" />{t('searchV2.compare.title')} ({providers.length})</DialogTitle>
          <DialogDescription>
            {t('searchV2.compare.description')}
          </DialogDescription>
        </DialogHeader>
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="py-2"></th>
              {providers.map((p) => (
                <th key={p.clinic_id} className="py-2 px-2 text-left">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-md overflow-hidden border border-slate-200 bg-slate-50 flex-none">
                      {_logoSrc(p.logo) && <img src={_logoSrc(p.logo)} alt={p.clinic_name} className="w-full h-full object-cover" />}
                    </div>
                    <span className="font-semibold text-sm">{p.clinic_name}</span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row(t('searchV2.compare.rowLocation'), (p) => [p.location?.city, p.location?.country].filter(Boolean).join(', '))}
            {row(treatment ? t('searchV2.compare.rowPrice') : t('searchV2.compare.rowPriceGlobal'), (p) => typeof p.commercial?.price_min === 'number' ? eur(p.commercial.price_min) : '—')}
            {row(t('searchV2.compare.rowLanguages'), (p) => (p.languages || []).map((l) => fmt(l)).join(' · ') || '—')}
            {row(t('searchV2.compare.rowAccreditations'), (p) => (p.accreditations || []).filter((a) => a.is_verified_new && !a.is_self_attested).map((a) => a.short_label || a.slug.toUpperCase()).join(' · ') || '—')}
            {row(t('searchV2.compare.rowDoctors'), (p) => (p.doctors?.public_verified_doctors || []).length)}
            {row(t('searchV2.compare.rowPackages') + (treatment ? ` (${treatmentName || fmt(treatment)})` : ''), (p) => {
              // SRCH-V2-JURISDICTION-01 (P0) — Compare previously rendered
              // campaign titles and `campaign_price_hint` straight from
              // `active_campaigns_for_context` with NO surface gating, while
              // PackagesTab already gated the identical data. Same canonical
              // rules, same hook, no second resolver. A suppressed surface
              // renders an explicit "not shown here" string rather than an
              // em dash, so a patient is never told the clinic has no package.
              if (!campaignsAllowed) {
                return <span className="text-slate-400 italic">{t('searchV2.compare.packagesSuppressed')}</span>;
              }
              const all = p.commercial?.active_campaigns_for_context || [];
              const f = campaignsForTreatment(all, treatment);
              return f.length === 0 ? '—' : (
                <ul className="space-y-0.5">
                  {f.slice(0, 3).map((c) => (
                    <li key={c.campaign_id}>
                      {c.title}
                      {promoPriceAllowed && typeof c.campaign_price_hint === 'number' && (
                        <span className="text-emerald-700 font-semibold"> — {eur(c.campaign_price_hint)}</span>
                      )}
                    </li>
                  ))}
                </ul>
              );
            })}
            {row(t('searchV2.compare.rowReviews'), () => <span className="text-slate-400 italic">{t('searchV2.compare.reviewsPending')}</span>)}
          </tbody>
        </table>
      </DialogContent>
    </Dialog>
  );
}

// ── Empty Results Recovery (P9-C2) ────────────────────────────────────
// When the canonical result set is empty, offer recovery built ONLY from
// the filter state the patient already entered. Never fabricates
// providers, recommendations or counts, and never silently widens a
// filter — every widening is an explicit click. The treatment (the
// patient's primary intent) is always preserved.
const RESTRICTIVE_FILTERS = [
  { key: 'country', isSet: (f) => !!f.country, clear: { country: null } },
  { key: 'city', isSet: (f) => !!f.city, clear: { city: null } },
  { key: 'budget', isSet: (f) => f.budget_min != null || f.budget_max != null, clear: { budget_min: null, budget_max: null } },
  { key: 'provider_types', isSet: (f) => (f.provider_types || []).length > 0, clear: { provider_types: [] } },
  { key: 'trip_mode', isSet: (f) => !!f.trip_mode, clear: { trip_mode: null } },
  { key: 'languages', isSet: (f) => (f.languages || []).length > 0, clear: { languages: [] } },
  { key: 'accreditations', isSet: (f) => (f.accreditations || []).length > 0, clear: { accreditations: [] } },
  { key: 'doctor_specialty', isSet: (f) => (f.doctor_specialty || []).length > 0, clear: { doctor_specialty: [] } },
  { key: 'positioning_tag', isSet: (f) => (f.positioning_tag || []).length > 0, clear: { positioning_tag: [] } },
  { key: 'vip_service', isSet: (f) => (f.vip_service || []).length > 0, clear: { vip_service: [] } },
  { key: 'has_campaign', isSet: (f) => f.has_campaign === true, clear: { has_campaign: null } },
  { key: 'shortlist_only', isSet: (f) => f.shortlist_only === true, clear: { shortlist_only: null } },
];

export function EmptyResults({ filters, setFilters, categoryLabel, treatmentName }) {
  const { t } = useTranslation();
  if (!filters.treatment) {
    return (
      <div className="text-center text-slate-500 text-sm py-12" data-testid="search-v2-empty">
        {/* P0 §4 — Category narrows the treatment choice; it is NOT a provider
            filter. Saying so explicitly is what keeps the visible filter state
            and the actual search semantics from diverging. */}
        {filters.category && categoryLabel ? (
          <span data-testid="search-v2-category-needs-treatment-hint">
            {t('searchV2.category.selectTreatmentInCategory', { category: categoryLabel })}
          </span>
        ) : (
          <span data-testid="search-v2-select-treatment-hint">
            {t('searchV2.empty.selectTreatment')}
          </span>
        )}
      </div>
    );
  }
  const active = RESTRICTIVE_FILTERS.filter((f) => f.isSet(filters));
  const clearAllRestrictive = () => setFilters((p) => ({
    ...p,
    ...RESTRICTIVE_FILTERS.reduce((acc, f) => ({ ...acc, ...f.clear }), {}),
  }));

  return (
    <div className="py-10 px-4 flex flex-col items-center text-center" data-testid="search-v2-empty">
      <Search className="h-6 w-6 text-slate-300 mb-2" aria-hidden="true" />
      <p className="text-sm font-semibold text-slate-800" data-testid="search-v2-empty-headline">
        {t('searchV2.empty.headline', { treatment: treatmentName || fmt(filters.treatment) })}
      </p>
      {active.length === 0 ? (
        <p className="text-xs text-slate-500 mt-1 max-w-md" data-testid="search-v2-empty-no-filters-hint">
          {t('searchV2.empty.noFiltersHint')}
        </p>
      ) : (
        <>
          <p className="text-xs text-slate-500 mt-1 max-w-md" data-testid="search-v2-empty-hint">
            {t('searchV2.empty.hint')}
          </p>
          <div className="mt-3 flex flex-wrap gap-1.5 justify-center" data-testid="search-v2-empty-filter-chips">
            {active.map((f) => (
              <button
                key={f.key}
                type="button"
                onClick={() => setFilters((p) => ({ ...p, ...f.clear }))}
                className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:border-rose-300 hover:text-rose-600 transition-colors"
                data-testid={`search-v2-empty-clear-${f.key}`}
              >
                <X className="h-3 w-3" aria-hidden="true" />{t('searchV2.empty.remove', { filter: t(`searchV2.emptyFilters.${f.key}`) })}
              </button>
            ))}
          </div>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={clearAllRestrictive}
            className="mt-3 h-8 text-xs"
            data-testid="search-v2-empty-clear-all-btn"
          >
            <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('searchV2.empty.clearAllKeep', { treatment: treatmentName || fmt(filters.treatment) })}
          </Button>
        </>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────
export default function SearchV2Page() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  // SEARCH-V2-FINAL-01 — token read locally; never depend on a context
  // that could throw and crash the page.  `null` for anonymous users
  // is the explicit canonical anonymous state.
  const [token] = useState(() => _readUnifiedToken());
  const isAuth = !!token;
  const [providers, setProviders] = useState([]);
  // SSO-CLOSURE-D — { treatment, requestable, reason } from /search/providers.
  const [requestEligibility, setRequestEligibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState(() => {
    const sp = new URLSearchParams(location.search);
    // FILTER-DATA-HARDFIX-01: hydrate every visible filter axis from the
    // URL so a shared / refreshed link reproduces the exact same state.
    const num = (v) => (v == null || v === '' ? null : Number(v));
    return {
      treatment: sp.get('treatment') || null,
      // TCPN-CN1 (typeahead): hydrate `country` and `city` as separate
      // canonical filter keys.  Country is stored as ISO-2; the URL
      // may carry a legacy English-name `country=Turkey` which we
      // normalise on read.  Legacy `?destination=` deep-links are
      // resolved as country if recognised, else as city.  Owner-amended
      // mutual exclusion: if both `?country=` and `?city=` are present
      // in the URL, country wins and city is dropped.
      country: (() => {
        const _c = sp.get('country');
        if (_c) {
          const iso = normalizeCountryToISO2(_c);
          if (iso) return iso;
        }
        const _d = sp.get('destination');
        if (_d) {
          const iso = normalizeCountryToISO2(_d);
          if (iso) return iso;
        }
        return null;
      })(),
      city: (() => {
        // If country resolved above, suppress city per mutual exclusion.
        const _c = sp.get('country');
        if (_c && normalizeCountryToISO2(_c)) return null;
        const _d = sp.get('destination');
        if (_d && normalizeCountryToISO2(_d)) return null;
        return sp.get('city') || sp.get('destination') || null;
      })(),
      budget_min: num(sp.get('budget_min')),
      budget_max: num(sp.get('budget_max')),
      provider_types: sp.getAll('provider_types'),
      languages: sp.getAll('languages'),
      accreditations: sp.getAll('accreditations'),
      doctor_specialty: sp.getAll('doctor_specialty'),
      // SEARCH-V2-POSITIONING-VIP-01: hydrate repeatable canonical
      // discovery axes from URL.  Backend contract uses singular param
      // names (`positioning_tag`, `vip_service`) — preserved verbatim.
      positioning_tag: sp.getAll('positioning_tag'),
      vip_service: sp.getAll('vip_service'),
      has_campaign: sp.get('has_campaign') === 'true' ? true : null,
      // SRCH-V2-CATEGORY-01 — category is a TREATMENT-NARROWING selector.
      // The canonical search API (`ComplianceSummaryService.search`) exposes
      // NO category parameter, so this is never sent to the backend and is
      // never treated as a provider filter. Hydrated from URL so a category
      // deep link survives a reload.
      category: sp.get('category') || null,
      // SEARCH-V2-SHORTLIST-PIN-01: client-only intersection toggle.
      // Never sent to backend.
      shortlist_only: sp.get('shortlist_only') === 'true' ? true : null,
      // SEARCH-V2-TRIP-MODE-01: hydrate single-select trip_mode from URL.
      // Unknown values are dropped silently (defensive against legacy links).
      trip_mode: (() => {
        const v = sp.get('trip_mode');
        return TRIP_MODES.some((m) => m.id === v) ? v : null;
      })(),
    };
  });
  const [selected, setSelected] = useState(null);
  // SEARCH-V2-REQUEST-QUOTE-PARITY-01 — V2 quote-request shell state.
  // selectedClinicsV2 is the canonical SSOT for the V2 quote-request flow,
  // strictly independent from shortlist (heart).  Doctor modal also writes
  // to this array; actual POST happens ONLY from the sticky bar CTA.
  const [selectedClinicsV2, setSelectedClinicsV2] = useState([]);
  const [patientInquiryNote, setPatientInquiryNote] = useState('');
  // SEARCH-V2-PACKAGE-INTENT-01 — explicit package intent map.
  // { [clinic_id]: { campaign_id, title } }.  Overrides the per-clinic
  // active_campaign at submit time and appends the package title to the
  // INQUIRY_NOTE message for that one clinic.
  const [pendingPackageByClinic, setPendingPackageByClinic] = useState({});
  // SSO-A3 — optional doctor PREFERENCE per provider: { [clinic_id]: {doctor_id, full_name} }.
  // A preference, never an assignment: the provider still decides who treats.
  const [preferredDoctorByClinic, setPreferredDoctorByClinic] = useState({});
  const [submittingRequests, setSubmittingRequests] = useState(false);
  const { toast } = useToast();
  const [taxonomy, setTaxonomy] = useState({ treatments: [], categories: [], offeredTreatments: [], accreditations: [], languages: [], specialties: [], positioningTags: [], vipServices: [], error: false, degraded: [], offeringsUnavailable: false });
  const [shortlist, setShortlist] = useState([]);
  // SEARCH-V2-CENTER-COLLAPSE-01 — local toggle that hides the center
  // provider list and lets the right detail panel expand into the
  // freed space.  Not persisted (no localStorage, no URL).  Mobile is
  // unaffected (toggle rail renders only at lg+).
  const [centerCollapsed, setCenterCollapsed] = useState(false);
  // P8-A3 — presentation mode for the SAME canonical result set.
  // Purely presentational: it never re-fetches, never reorders and never
  // touches filters, selection, shortlist or quote state.
  const [viewMode, setViewMode] = useState('list');
  // P7-B2 — read-only canonical treatment catalogue viewer.
  const [treatmentDetailsOpen, setTreatmentDetailsOpen] = useState(false);
  const [shortlistDetails, setShortlistDetails] = useState({}); // clinic_id → provider
  const [compareOpen, setCompareOpen] = useState(false);
  const [doctorModal, setDoctorModal] = useState(null);
  const [accViewer, setAccViewer] = useState(null); // { accs, index }
  const [galleryViewer, setGalleryViewer] = useState(null); // { items, index }
  const [showFiltersMobile, setShowFiltersMobile] = useState(false);

  // SRCH-V2-CATEGORY-01 — a deep link carrying only `?treatment=` initialises
  // its Category once the canonical taxonomy resolves. If the treatment has no
  // category mapping, the selection is preserved and the rail says so; it is
  // never silently discarded.
  useEffect(() => {
    if (filters.category || !filters.treatment) return;
    // SV2-OFFER-SCOPE-UNIFY-01 — canonical comparison, so an alias deep-link
    // initialises its category exactly as the hyphen form does.
    const want = normalizeTreatmentSlug(filters.treatment);
    const hit = (taxonomy.treatments || []).find(
      (x) => normalizeTreatmentSlug(x.slug) === want,
    );
    if (hit?.category_slug) {
      setFilters((prev) => (prev.category ? prev : { ...prev, category: hit.category_slug }));
    }
  }, [filters.treatment, filters.category, taxonomy.treatments]);

  const activeCategoryLabel = useMemo(
    () => (taxonomy.categories || []).find((c) => c.slug === filters.category)?.label || null,
    [taxonomy.categories, filters.category],
  );

  // Sync URL <- filters  (FILTER-DATA-HARDFIX-01: every visible axis)
  useEffect(() => {
    const sp = new URLSearchParams();
    if (filters.treatment) sp.set('treatment', filters.treatment);
    // TCPN-CN1 (typeahead): country and city are independent, mutually
    // exclusive within the picker.  Only one is ever populated at a time
    // from a Destination selection — but if both happen to be set (e.g.
    // a legacy URL), both are emitted faithfully.
    if (filters.country) sp.set('country', filters.country);
    if (filters.city) sp.set('city', filters.city);
    if (filters.budget_min != null) sp.set('budget_min', String(filters.budget_min));
    if (filters.budget_max != null) sp.set('budget_max', String(filters.budget_max));
    (filters.provider_types || []).forEach((v) => sp.append('provider_types', v));
    (filters.languages || []).forEach((v) => sp.append('languages', v));
    (filters.accreditations || []).forEach((v) => sp.append('accreditations', v));
    (filters.doctor_specialty || []).forEach((v) => sp.append('doctor_specialty', v));
    // SEARCH-V2-POSITIONING-VIP-01: emit repeatable canonical params.
    (filters.positioning_tag || []).forEach((v) => sp.append('positioning_tag', v));
    (filters.vip_service || []).forEach((v) => sp.append('vip_service', v));
    if (filters.has_campaign === true) sp.set('has_campaign', 'true');
    // SRCH-V2-CATEGORY-01: emit the narrowing selector so deep links round-trip.
    if (filters.category) sp.set('category', filters.category);
    // SEARCH-V2-SHORTLIST-PIN-01: emit shortlist_only when ON; omit
    // otherwise.  Never appended to the /api/search/providers request.
    if (filters.shortlist_only === true) sp.set('shortlist_only', 'true');
    // SEARCH-V2-TRIP-MODE-01: emit single-select trip_mode to URL.
    if (filters.trip_mode) sp.set('trip_mode', filters.trip_mode);
    const qs = sp.toString();
    navigate({ pathname: '/search-v2', search: qs ? `?${qs}` : '' }, { replace: true });
  }, [
    filters.treatment,
    filters.country,
    filters.city,
    filters.budget_min,
    filters.budget_max,
    filters.has_campaign,
    filters.category,
    filters.shortlist_only,
    filters.trip_mode,
    (filters.provider_types || []).join('|'),
    (filters.languages || []).join('|'),
    (filters.accreditations || []).join('|'),
    (filters.doctor_specialty || []).join('|'),
    (filters.positioning_tag || []).join('|'),
    (filters.vip_service || []).join('|'),
  ]);  // eslint-disable-line

  // FILTER-DATA-HARDFIX-01: load taxonomy from the canonical endpoints.
  // No hardcoded fallbacks. On failure, `error: true` is surfaced so the
  // FilterRail can render a visible inline error rather than silently
  // presenting empty lists.
  //
  // SRCH-V2-TAXONOMY-RESILIENCE-01 (P1 §8) — the same seven canonical
  // endpoints, but settled INDEPENDENTLY. Previously a single `Promise.all`
  // meant one failing auxiliary catalogue (say VIP services) rejected the
  // whole batch and replaced every list with `[]` plus `error: true`, wiping
  // out treatments and categories that had loaded perfectly well. Three
  // distinct failure classes are now tracked separately:
  //   • CORE  (treatment taxonomy) — search cannot run; blocking error + retry
  //   • OPTIONAL (filter catalogues) — those filters hide, search still works
  //   • OFFERINGS (provider-enabled list) — the offered/not-offered split is
  //     withheld rather than labelling every treatment either way
  // No new aggregation layer and no second taxonomy authority: the same
  // endpoints, the same single state owner, the same shapes.
  // SRCH-V2-TAXONOMY-PUBLISH-01 (perf) — the seven requests were awaited in a
  // single `Promise.all`, so the filter rail and the treatment picker stayed
  // empty until the SLOWEST optional catalogue answered, even though the core
  // taxonomy had arrived first. Each catalogue now publishes its own slice of
  // the SAME single state as soon as its own request resolves; the real
  // dependency (a treatment list before anything can be searched for) is
  // preserved because `error` is still decided by the core response alone, and
  // an optional failure can no longer discard content that loaded correctly.
  const [taxonomyReloadKey, setTaxonomyReloadKey] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const get = (url) => axios.get(url).then((r) => r.data).catch(() => null);
    const publish = (patch) => {
      if (!cancelled) setTaxonomy((prev) => ({ ...prev, ...patch(prev) }));
    };

    // CORE — without it there is nothing to search for. Published on its own.
    (async () => {
      const trxData = await get(
        `${API}/api/public/treatment-taxonomy?language=${encodeURIComponent(i18n.language || 'en')}`,
      );
      if (cancelled) return;
      const trx = (trxData?.treatments || [])
        .filter((x) => (x.status ? x.status === 'active' : true))
        .map((x) => ({
          slug: x.slug,
          name: x.display_name || x.name || fmt(x.slug),
          category_slug: x.category_slug,
        }));
      const categories = (trxData?.categories || [])
        .filter((c) => (c.status ? c.status === 'active' : true))
        .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
        .map((c) => ({ slug: c.slug, label: c.label || c.name?.en || fmt(c.slug) }));
      const coreFailed = !trxData || trx.length === 0;
      if (coreFailed) {
        // eslint-disable-next-line no-console
        console.error('Search V2 core taxonomy unavailable');
      }
      publish(() => ({ treatments: trx, categories, error: coreFailed }));
    })();

    // OFFERINGS — an unreachable endpoint must NOT be read as "no provider
    // offers anything". `offeredTreatments: []` already means "unknown" to the
    // picker (it falls back to listing the full catalogue ungrouped), so the
    // distinction is carried explicitly for the UI notice.
    (async () => {
      const offeredData = await get(`${API}/api/public/treatments`);
      if (cancelled) return;
      if (!offeredData) {
        // eslint-disable-next-line no-console
        console.error('Search V2 provider offerings unavailable');
      }
      publish(() => ({
        offeredTreatments: (offeredData?.treatments || []).map((x) => x.slug),
        offeringsUnavailable: !offeredData,
      }));
    })();

    // OPTIONAL filter catalogues — each settles independently; a failure hides
    // only its own filter and records only its own `degraded` entry.
    const optional = [
      {
        key: 'accreditations',
        url: `${API}/api/public/master/accreditations`,
        pick: (d) => (d?.accreditations || [])
          .filter((a) => a.is_active !== false)
          .map((a) => ({
            slug: a.slug,
            short_label: a.short_label,
            long_label: a.long_label,
            name: a.name || a.long_label || a.short_label,
          })),
      },
      {
        key: 'languages',
        url: `${API}/api/public/master/languages`,
        pick: (d) => (d?.languages || [])
          .filter((l) => l.is_active !== false)
          .map((l) => ({ slug: l.slug, name: l.name })),
      },
      {
        key: 'specialties',
        url: `${API}/api/public/master/specialties`,
        pick: (d) => (d?.specialties || [])
          .filter((x) => x.is_active !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((x) => ({ slug: x.slug, name: x.name || fmt(x.slug) })),
      },
      {
        key: 'positioningTags',
        url: `${API}/api/public/positioning-tags`,
        pick: (d) => (d?.tags || [])
          .filter((x) => (x.status ? x.status === 'active' : true) && x.is_filterable !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((x) => ({ slug: x.slug, name: x.display || x.name || fmt(x.slug) })),
      },
      {
        key: 'vipServices',
        url: `${API}/api/public/vip-services`,
        pick: (d) => (d?.services || [])
          .filter((x) => (x.status ? x.status === 'active' : true) && x.is_filterable !== false)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
          .map((x) => ({ slug: x.slug, name: x.name || x.display || fmt(x.slug) })),
      },
    ];
    optional.forEach(({ key, url, pick }) => {
      (async () => {
        const data = await get(url);
        if (cancelled) return;
        if (!data) {
          // eslint-disable-next-line no-console
          console.error('Search V2 taxonomy catalogue unavailable:', key);
        }
        publish((prev) => ({
          [key]: data ? pick(data) : [],
          degraded: data
            ? (prev.degraded || []).filter((k) => k !== key)
            : Array.from(new Set([...(prev.degraded || []), key])),
        }));
      })();
    });

    return () => { cancelled = true; };
  }, [taxonomyReloadKey, i18n.language]);

  // Fetch search results — FILTER-DATA-HARDFIX-01: canonical query
  // parameters per /api/search/providers contract:
  //   accreditation  (repeatable, OR)   ← was wrongly sent as `accreditations`
  //   language       (repeatable, OR)   ← was wrongly sent as `languages`
  //   city           (single)           ← was wrongly sent as `destination`
  //   min_price / max_price             ← was wrongly sent as budget_min/_max
  //   provider_type  (repeatable, OR)   ← PRD1.9: now a real backend filter
  useEffect(() => {
    let cancelled = false;
    // SRCH-V2-REQUEST-ABORT-01 (perf) — a superseded response was only
    // IGNORED, so every abandoned filter combination still occupied a
    // connection and was parsed in full. The obsolete request is now aborted
    // through the HTTP client's own cancellation support, while the
    // `cancelled` guard is kept so a response already in flight can never
    // overwrite newer results. An aborted request is treated as a
    // cancellation, never as a user-visible failure. NOTE: aborting the HTTP
    // request does not necessarily stop a database query already executing on
    // the server — this removes client waiting and parsing, nothing more.
    const controller = new AbortController();
    // FUNNEL-INTEGRITY — no treatment intent → no provider ranking. Search loads
    // its filters, but Providers are only listed once a specific treatment is chosen.
    if (!filters.treatment) {
      setProviders([]);
      setLoading(false);
      return () => { cancelled = true; controller.abort(); };
    }
    (async () => {
      setLoading(true);
      try {
        const sp = new URLSearchParams();
        if (filters.treatment) sp.set('treatment', filters.treatment);
        // TCPN-CN1 (typeahead): the picker has already classified the
        // axis at selection time; we just forward whichever key is set.
        // Owner amendment guarantees these are mutually exclusive in
        // the picker flow.
        if (filters.country) sp.set('country', filters.country);
        if (filters.city) sp.set('city', filters.city);
        if (filters.has_campaign === true) sp.set('has_campaign', 'true');
        if (filters.budget_min != null) sp.set('min_price', String(filters.budget_min));
        if (filters.budget_max != null) sp.set('max_price', String(filters.budget_max));
        (filters.languages || []).forEach((l) => sp.append('language', l));
        // SEARCH-V2-LANG-AND-01: Search V2 uses AND semantics for the
        // language filter (clinic must support every selected language).
        // V1 omits this param and keeps the historical OR contract.
        if ((filters.languages || []).length > 0) {
          sp.set('language_match', 'all');
        }
        (filters.accreditations || []).forEach((a) => sp.append('accreditation', a));
        (filters.doctor_specialty || []).forEach((s) => sp.append('doctor_specialty', s));
        // PRD1.9: send canonical repeatable provider_type filter to backend.
        // Client-side narrowing has been removed — this is now server-side.
        (filters.provider_types || []).forEach((t) => sp.append('provider_type', t));
        // SEARCH-V2-POSITIONING-VIP-01: canonical discovery filters.
        (filters.positioning_tag || []).forEach((t) => sp.append('positioning_tag', t));
        (filters.vip_service || []).forEach((v) => sp.append('vip_service', v));
        // SEARCH-V2-TRIP-MODE-01: forward single-select trip_mode to backend.
        if (filters.trip_mode) sp.set('trip_mode', filters.trip_mode);
        sp.set('limit', '50');
        const res = await axios.get(`${API}/api/search/providers?${sp.toString()}`,
          { signal: controller.signal });
        if (!cancelled) {
          const rows = res.data?.providers || [];
          setProviders(rows);
          // SSO-CLOSURE-D — the backend reports whether the canonical request
          // endpoint will accept this treatment, using the same predicate the
          // endpoint itself applies. Surfaced so the UI never invites a
          // request that is certain to be rejected.
          setRequestEligibility(res.data?.request_eligibility || null);
          // STABILIZATION-01 amendment 2: keep selection consistent
          // with the visible result set.
          if (rows.length === 0) {
            setSelected(null);
          } else if (!rows.find((p) => p.clinic_id === selected?.clinic_id)) {
            setSelected(rows[0]);
          }
        }
      } catch (e) {
        // A cancellation is not an error: the newer request owns the state.
        const aborted = axios.isCancel?.(e) || e?.code === 'ERR_CANCELED'
          || e?.name === 'CanceledError' || e?.name === 'AbortError';
        if (!cancelled && !aborted) { setProviders([]); setRequestEligibility(null); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; controller.abort(); };
  }, [filters.treatment, filters.country, filters.city, filters.has_campaign, filters.budget_min, filters.budget_max, filters.trip_mode, (filters.languages || []).join('|'), (filters.accreditations || []).join('|'), (filters.doctor_specialty || []).join('|'), (filters.provider_types || []).join('|'), (filters.positioning_tag || []).join('|'), (filters.vip_service || []).join('|')]);  // eslint-disable-line

  // Hydrate canonical shortlist on mount
  useEffect(() => {
    if (!isAuth) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/api/patient/shortlist`);
        const items = r.data?.items || r.data?.clinics || [];
        setShortlist(items.map((x) => x.clinic_id || x));
      } catch (e) { /* canonical empty state */ }
    })();
  }, [isAuth, token]);

  // Keep shortlist details cache updated from the current providers list
  useEffect(() => {
    if (!providers.length) return;
    setShortlistDetails((p) => {
      const next = { ...p };
      providers.forEach((pr) => {
        if (shortlist.includes(pr.clinic_id)) next[pr.clinic_id] = pr;
      });
      return next;
    });
  }, [providers, shortlist]);

  const toggleShortlist = useCallback(async (cid) => {
    if (!isAuth) {
      // canonical lightweight anonymous path (mirrors V1 + S05)
      setShortlist((p) => (p.includes(cid) ? p.filter((x) => x !== cid) : [...p, cid]));
      return;
    }
    const next = shortlist.includes(cid) ? shortlist.filter((x) => x !== cid) : [...shortlist, cid];
    setShortlist(next);
    try {
      if (next.includes(cid)) {
        await axios.post(`${API}/api/patient/shortlist/${cid}`, { source: 'search-v2' });
      } else {
        await axios.delete(`${API}/api/patient/shortlist/${cid}`);
      }
    } catch (e) { /* keep optimistic state */ }
  }, [isAuth, token, shortlist]);

  const shortlistedProviders = useMemo(() => {
    return shortlist
      .map((cid) => shortlistDetails[cid] || providers.find((p) => p.clinic_id === cid))
      .filter(Boolean);
  }, [shortlist, shortlistDetails, providers]);

  // SEARCH-V2-REQUEST-QUOTE-PARITY-01 — hydrate post-anonymous-redirect.
  // On authenticated mount, restore selectedClinicsV2 + patientInquiryNote
  // from V2-isolated sessionStorage keys and clear them.  Idempotent.
  useEffect(() => {
    if (!isAuth) return;
    try {
      const raw = sessionStorage.getItem(SESSION_STORAGE_KEY_SELECTION_V2);
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (Array.isArray(parsed) && parsed.length > 0) {
            setSelectedClinicsV2(parsed.filter((x) => typeof x === 'string'));
          }
        } catch (_) { /* malformed payload — ignore */ }
        sessionStorage.removeItem(SESSION_STORAGE_KEY_SELECTION_V2);
      }
      const note = sessionStorage.getItem(SESSION_STORAGE_KEY_NOTE_V2);
      if (note !== null && note !== undefined) {
        setPatientInquiryNote(String(note).slice(0, PATIENT_INQUIRY_NOTE_MAX_V2));
        sessionStorage.removeItem(SESSION_STORAGE_KEY_NOTE_V2);
      }
    } catch (_) { /* storage unavailable — no-op */ }
  }, [isAuth]);

  const toggleClinicSelectionV2 = useCallback((cid) => {
    if (!cid) return;
    setSelectedClinicsV2((prev) => prev.includes(cid)
      ? prev.filter((x) => x !== cid)
      : [...prev, cid]);
    // SEARCH-V2-PACKAGE-INTENT-01 — also drop any pending package intent
    // when the clinic is being removed.
    setPendingPackageByClinic((prev) => {
      if (!prev[cid]) return prev;
      const next = { ...prev };
      delete next[cid];
      return next;
    });
  }, []);
  const clearSelectedProvidersV2 = useCallback(() => {
    setSelectedClinicsV2([]);
    setPendingPackageByClinic({});
    setPreferredDoctorByClinic({});
  }, []);

  // SSO-CLOSURE-D — only `false` blocks. `true` and `null`/unknown do not,
  // so a probe failure can never silently disable the whole funnel.
  const requestNotAccepted = !!filters.treatment
    && requestEligibility?.treatment === filters.treatment
    && requestEligibility?.requestable === false;

  // SV2-ACCEPT-01 — an UNKNOWN eligibility (the probe itself failed) must not
  // read as a confirmed "yes". The request stays possible — blocking the funnel
  // on a probe outage would be worse — but the patient is told the acceptance
  // is unconfirmed instead of being promised silently.
  const requestEligibilityUnknown = !!filters.treatment
    && requestEligibility?.treatment === filters.treatment
    && requestEligibility?.requestable === null;

  // SV2-ACCEPT-01 — canonical catalogue name for the searched treatment.
  const treatmentDisplayName = useMemo(
    () => treatmentNameOf(taxonomy, filters.treatment),
    [taxonomy, filters.treatment],
  );

  // SV2-ACCEPT-01 — `selected` serves BOTH the always-mounted desktop detail
  // column and the full-screen mobile overlay. The result effect auto-selects
  // row 0 so the desktop column is never empty, which meant a phone landed on
  // a provider detail sheet before the patient tapped anything and the result
  // list was unreachable until it was dismissed. The overlay now opens only on
  // an explicit tap; desktop behaviour is unchanged.
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const openProviderDetail = useCallback((p) => {
    setSelected(p);
    setMobileDetailOpen(true);
  }, []);

  // SSO-A3 — toggle an optional doctor preference for the open provider.
  // Selecting a doctor expresses a WISH; the provider still assigns.
  const handlePreferDoctor = useCallback((doctor) => {
    const cid = selected?.clinic_id;
    if (!cid || !doctor?.doctor_id) return;
    setPreferredDoctorByClinic((prev) => {
      const next = { ...prev };
      if (next[cid]?.doctor_id === doctor.doctor_id) {
        delete next[cid];
      } else {
        next[cid] = { doctor_id: doctor.doctor_id, full_name: doctor.full_name || '' };
      }
      return next;
    });
  }, [selected]);

  // Canonical submitQuoteRequests — single quote-request submission flow.
  const [residencePromptOpen, setResidencePromptOpen] = useState(false);
  const submitQuoteRequestsV2 = useCallback(async () => {
    setSubmittingRequests(true);
    try {
      const ids = [...selectedClinicsV2];
      const trimmedNote = (patientInquiryNote || '').trim().slice(0, PATIENT_INQUIRY_NOTE_MAX_V2);
      const baseMessage = trimmedNote.length >= 5 ? trimmedNote : 'Patient requested offer via TreatX search';
      const body = {
        message: baseMessage,
        // SV2-ACCEPT-01 — submit the canonical slug the server resolved for
        // this search (alias deep-links such as `hair_transplant_fue_dhi`
        // match providers but are not the catalogue slug the request
        // validator accepts).
        treatment_interest: (requestEligibility?.treatment === filters.treatment
          && requestEligibility?.canonical_treatment)
          || filters.treatment
          || null,
        preferred_contact: ['platform'],
      };
      const settled = await Promise.all(
        ids.map(async (cid) => {
          try {
            // SSO-A1 (audit defect D3) — an offer that is merely DISPLAYED is
            // not an offer the patient chose. Only an explicit click on this
            // provider's International Offer CTA attributes the request; the
            // former `has_active_campaign → active_campaign_id` fallback made
            // every request promotional and left no way to ask for the
            // provider's standard treatment.
            const overridePkg = pendingPackageByClinic[cid];
            const explicitSelection = !!(overridePkg && overridePkg.campaign_id);
            const campaignId = explicitSelection ? overridePkg.campaign_id : null;
            const doctorPref = preferredDoctorByClinic[cid];
            const perMessage = overridePkg && overridePkg.title
              ? `${baseMessage}\n(Package interest: ${overridePkg.title})`.slice(0, 2000)
              : baseMessage;
            const perClinicBody = {
              ...body,
              message: perMessage,
              // STAGE-3G-C3 — declare WHICH pathway produced `campaign_id`.
              // Post-SSO-A1 only the explicit pathway can produce one at all;
              // the token is still sent so the backend never has to infer it.
              ...(campaignId
                ? { campaign_id: campaignId, campaign_selection: 'explicit' }
                : {}),
              // SSO-A3 — optional, non-binding doctor preference.
              ...(doctorPref?.doctor_id ? { preferred_doctor_id: doctorPref.doctor_id } : {}),
            };
            // TREATX-BUG4-FIX (2026-02) — use authenticated `api` client.
            // The prior `axios.post` sent NO Authorization header, so the
            // backend `/public/clinics/{cid}/request-quote` handler at
            // `routes/quote_request_routes.py:183-190` rejected the call
            // with UNAUTHORIZED. The endpoint is under `/public/` for
            // clinic-lookup semantics but still requires a patient JWT to
            // establish which patient the request belongs to.
            const res = await api.post(
              `/public/clinics/${cid}/request-quote`,
              perClinicBody,
            );
            const data = res?.data || {};
            const ok = !!(data.success || data.lead_id);
            const reused = !!(data.reused_existing === true);
            // SSO-C — three distinct outcomes, never conflated.
            const status = data.status || (reused ? 'updated' : 'created');
            return { cid, ok, reused, status, previousOfferStatus: data.previous_offer_status || null, lead_id: data.lead_id || null, thread_id: data.thread_id || null, error: ok ? null : (data.detail || 'Request rejected') };
          } catch (err) {
            const detail = err?.response?.data?.detail || err?.message || 'Network error';
            return { cid, ok: false, reused: false, status: 'failed', lead_id: null, thread_id: null, error: detail, code: err?.response?.data?.error || null };
          }
        })
      );
      const successes = settled.filter((s) => s.ok);
      const created = successes.filter((s) => s.status === 'created');
      const renewed = successes.filter((s) => s.status === 'renewed');
      const updated = successes.filter((s) => s.status === 'updated');
      const unchanged = successes.filter((s) => s.status === 'unchanged');
      const reused = successes.filter((s) => s.reused);
      const failures = settled.filter((s) => !s.ok);
      if (failures.some((f) => f.code === 'RESIDENCE_REQUIRED' || String(f.error || '').toLowerCase().includes('country of residence'))) {
        setResidencePromptOpen(true);
        return;
      }
      if (successes.length === 0) {
        // SV2-ELIGIBILITY-UX-01 — the canonical validator is authoritative and
        // its rejection text is server-side English. When it rejects on
        // treatment grounds (the only state Search itself can anticipate), show
        // the patient the SAME translated sentence the disabled-CTA state uses,
        // so a TR patient is never handed an English catalogue message. The
        // rejection itself is unchanged — nothing is retried or bypassed.
        const first = failures[0];
        const description = TREATMENT_REJECTION_CODES.includes(first?.code)
          ? t('searchV2.selection.notRequestable')
          : (first?.error || t('searchV2.toast.unknownError'));
        toast({ title: t('searchV2.toast.requestFailed'), description, variant: 'destructive' });
        return;
      }
      if (failures.length > 0) {
        toast({ title: t('searchV2.toast.partialFailure'), description: t('searchV2.toast.partialDetail', { sent: created.length, reused: reused.length, failed: failures.length }), variant: 'destructive' });
      } else if (created.length === 0 && renewed.length > 0) {
        // RENEWAL-CYCLE-01 — the previous offer had expired, so this really is a
        // new request on the existing conversation. Say exactly that.
        // The reason must match what the patient sees on the offer itself:
        // a declined offer must never be described as expired.
        const declined = renewed.every((r) => String(r.previousOfferStatus || '').toUpperCase() === 'REJECTED');
        toast({
          title: t('searchV2.toast.requestRenewed', { count: renewed.length }),
          description: declined ? t('searchV2.toast.requestRenewedDetailDeclined') : t('searchV2.toast.requestRenewedDetail'),
        });
      } else if (created.length === 0 && updated.length > 0) {
        // SSO-C — the provider DID receive the new message; say so truthfully.
        toast({ title: t('searchV2.toast.requestUpdated', { count: updated.length }), description: t('searchV2.toast.requestUpdatedDetail') });
      } else if (created.length === 0 && unchanged.length > 0) {
        toast({ title: t('searchV2.toast.alreadyActive'), description: t('searchV2.toast.alreadyActiveDetail') });
      } else if (reused.length > 0) {
        toast({ title: t('searchV2.toast.sent', { count: created.length }), description: t('searchV2.toast.sentReused', { count: reused.length }) });
      } else {
        toast({ title: t('searchV2.toast.sent', { count: created.length }) });
      }
      setSelectedClinicsV2([]);
      setPatientInquiryNote('');
      setPendingPackageByClinic({});
      setPreferredDoctorByClinic({});
      try {
        sessionStorage.removeItem(SESSION_STORAGE_KEY_SELECTION_V2);
        sessionStorage.removeItem(SESSION_STORAGE_KEY_NOTE_V2);
        sessionStorage.removeItem(SESSION_STORAGE_KEY_TREATMENT_V2);
        sessionStorage.removeItem(SESSION_STORAGE_KEY_PACKAGE_INTENT_V2);
      } catch (_) { /* storage unavailable — best-effort cleanup */ }
      navigate('/patient/requests', { state: { fromSearchSubmit: true, count: successes.length } });
    } finally {
      setSubmittingRequests(false);
    }
  }, [selectedClinicsV2, patientInquiryNote, filters.treatment, requestEligibility, providers, token, toast, navigate, pendingPackageByClinic, preferredDoctorByClinic]);

  const handleSubmitRequestsV2 = useCallback(async () => {
    if (!filters.treatment) {
      toast({
        title: t('searchV2.toast.selectTreatmentFirst'),
        description: t('searchV2.toast.selectTreatmentFirstDetail'),
        variant: 'destructive',
      });
      return;
    }
    if (selectedClinicsV2.length === 0) {
      toast({ title: t('searchV2.toast.noClinics'), description: t('searchV2.toast.noClinicsDetail'), variant: 'destructive' });
      return;
    }
    if (!isAuth) {
      try {
        sessionStorage.setItem(SESSION_STORAGE_KEY_SELECTION_V2, JSON.stringify(selectedClinicsV2));
        sessionStorage.setItem(SESSION_STORAGE_KEY_NOTE_V2, patientInquiryNote || '');
        sessionStorage.setItem(SESSION_STORAGE_KEY_TREATMENT_V2, filters.treatment || '');
        sessionStorage.setItem(SESSION_STORAGE_KEY_PACKAGE_INTENT_V2, JSON.stringify(pendingPackageByClinic || {}));
      } catch (_) { /* storage unavailable — anon redirect still proceeds */ }
      navigate(ANON_REDIRECT_V2, { replace: false });
      return;
    }
    // FUNNEL-INTEGRITY — canonical residence must exist BEFORE any lead is created.
    // Backend enforces the same (409 RESIDENCE_REQUIRED); this only resolves it in-context.
    try {
      const { data } = await api.get('/surface/residence');
      if (!data?.declared) {
        setResidencePromptOpen(true);
        return;
      }
    } catch (_) { /* fall through — backend still enforces */ }
    await submitQuoteRequestsV2();
  }, [filters.treatment, selectedClinicsV2, patientInquiryNote, pendingPackageByClinic, isAuth, navigate, submitQuoteRequestsV2, toast]);
  const onResidenceDeclared = useCallback(async () => {
    setResidencePromptOpen(false);
    await submitQuoteRequestsV2();
  }, [submitQuoteRequestsV2]);

  // SEARCH-V2-SHORTLIST-PIN-01 — client-only pipeline:
  //   backend providers
  //     → [Phase 2] shortlist_only filter (no-op when toggle OFF)
  //     → [Phase 1] stable-partition sort (shortlisted first, backend
  //                 order preserved within each group)
  // No backend coupling.  Stable `Array.prototype.sort` per ES2019.
  const shortlistSet = useMemo(() => new Set(shortlist), [shortlist]);
  const shortlistFilteredProviders = useMemo(() => {
    if (filters.shortlist_only !== true) return providers;
    return providers.filter((p) => shortlistSet.has(p.clinic_id));
  }, [providers, filters.shortlist_only, shortlistSet]);
  const sortedProviders = useMemo(() => {
    if (shortlistSet.size === 0) return shortlistFilteredProviders;
    return [...shortlistFilteredProviders].sort(
      (a, b) => Number(shortlistSet.has(b.clinic_id)) - Number(shortlistSet.has(a.clinic_id)),
    );
  }, [shortlistFilteredProviders, shortlistSet]);

  // STAGE-3G-OFFER-RELEVANCE-01 — canonical treatment membership for the
  // selected category, read from the taxonomy this page already loaded
  // (`/api/public/treatment-taxonomy`). No new endpoint, no extra request, and
  // no catalogue read re-introduced into the search path. Empty while a
  // specific treatment is selected, because the treatment takes precedence; a
  // category whose membership cannot be resolved yields an EMPTY set, which the
  // hook treats as "category active, nothing relevant" — never provider-wide.
  // STAGE-3G-B2-HOTFIX-02 — the taxonomy is mapped into state with
  // `category_slug` (see the treatment picker and the mapper below); reading
  // `tx.category` was always undefined, so membership resolved empty for every
  // category.
  const offerCategorySlugs = useMemo(() => {
    if (filters.treatment || !filters.category) return null;
    const norm = (s) => String(s || '').trim().toLowerCase().replace(/_/g, '-');
    const want = norm(filters.category);
    const slugs = (taxonomy?.treatments || [])
      .filter((tx) => norm(tx?.category_slug) === want)
      .map((tx) => norm(tx?.slug))
      .filter(Boolean);
    return new Set(slugs);
  }, [filters.treatment, filters.category, taxonomy?.treatments]);


  // SEARCH-V2-PACKAGE-INTENT-01 — Package "Request offer" CTA.
  // Adds the currently-selected clinic to selectedClinicsV2 (additive,
  // no duplicate), and records the clicked package's campaign_id +
  // title as explicit intent for that clinic.  Does NOT POST.  Actual
  // submission still happens only from the sticky bar CTA.
  const handleRequest = useCallback((camp) => {
    const cid = selected?.clinic_id || selected?.provider_id;
    if (!cid || !camp) return;
    setSelectedClinicsV2((prev) => (prev.includes(cid) ? prev : [...prev, cid]));
    setPendingPackageByClinic((prev) => ({
      ...prev,
      [cid]: { campaign_id: camp.campaign_id || null, title: camp.title || null },
    }));
    toast({
      title: t('searchV2.toast.addedToSelection'),
      description: camp.title || t('searchV2.toast.addedToSelectionDetail'),
    });
  }, [selected, toast]);

  return (
    <div className="h-full bg-slate-100 flex flex-col" data-testid="search-v2-page">
      <ResidencePromptDialog open={residencePromptOpen} onOpenChange={setResidencePromptOpen} onDeclared={onResidenceDeclared} />
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 px-4 py-2 flex items-center gap-3 flex-shrink-0">
        <div className="flex items-center gap-1.5">
          <Search className="h-4 w-4 text-slate-500" />
          <span className="font-semibold text-slate-800 text-sm">{t('searchV2.title')}</span>
        </div>
        <div className="ml-auto flex items-center gap-2">
          {/* P9-C1 — Compare eligibility is stated, not implied. The
              canonical mechanism is unchanged: comparison operates on the
              SHORTLIST (heart icon), never on the quote selection. */}
          <span className="hidden sm:block text-[11px] text-slate-500" data-testid="search-v2-compare-eligibility-hint">
            {shortlistedProviders.length < 2
              ? t('searchV2.compare.needMore', { count: 2 - shortlistedProviders.length })
              : shortlistedProviders.length > 3
                ? t('searchV2.compare.tooMany')
                : t('searchV2.compare.ready', { count: shortlistedProviders.length })}
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCompareOpen(true)}
            disabled={shortlistedProviders.length < 2 || shortlistedProviders.length > 3}
            title={shortlistedProviders.length < 2 || shortlistedProviders.length > 3
              ? t('searchV2.compare.disabledTitle')
              : t('searchV2.compare.enabledTitle')}
            data-testid="search-v2-compare-btn"
          >
            <GitCompare className="h-4 w-4 mr-1" />
            {t('searchV2.compare.button')} ({shortlistedProviders.length})
          </Button>
        </div>
      </header>

      <ActiveFilterBar filters={filters} setFilters={setFilters} taxonomy={taxonomy} />

      {/* Main 3-column layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Filter Rail — hidden on mobile unless toggled */}
        {/* SV2-IPAD-LAYOUT-01 — the 3-column composition turns on at `xl`
            (1280px), not `lg` (1024px). On iPad landscape (1024-1180px) the
            rail + 460px detail panel squeezed the result column to 234-390px
            while a row needs ~470px, and the column clips (`overflow-x:
            hidden`), so the select / save controls sat outside it: the owner
            could not scroll the centre panel horizontally and could not
            select, compare or request. Below `xl` the existing single-column
            list + tap-to-open detail overlay is used — the same layout iPad
            portrait and phones already use. */}
        {/* TREATX-SEARCH-TABLET-FILTERS-01 — the inline rail now appears from
            `lg` (1024px) instead of `xl` (1280px). iPad landscape reports
            1180 CSS px, so the entire Refine panel — including the treatment
            picker — used to be hidden behind a small "Filters" button there
            while desktop Chrome showed it permanently; that asymmetry is what
            made the same filter look unavailable on iPad. The `xl`-gated
            460px detail panel below is untouched, so the squeeze this comment
            describes cannot reappear: between `lg` and `xl` the provider
            detail still opens as the existing full-screen overlay. */}
        <div className={`${showFiltersMobile ? 'block fixed inset-0 z-[60] bg-white overflow-y-auto' : 'hidden'} lg:block lg:relative lg:z-auto lg:h-full`}>
          {showFiltersMobile && (
            <div className="lg:hidden p-3 border-b border-slate-200 flex items-center justify-between bg-white">
              <p className="font-semibold text-sm">Filters</p>
              <Button size="sm" variant="ghost" onClick={() => setShowFiltersMobile(false)}><X className="h-4 w-4" /></Button>
            </div>
          )}
          <FilterRail filters={filters} setFilters={setFilters} taxonomy={taxonomy} onRetryTaxonomy={() => setTaxonomyReloadKey((k) => k + 1)} />
        </div>

        {/* Provider grid */}
        <main className={centerCollapsed ? 'hidden' : 'flex-1 min-w-0 overflow-y-auto bg-slate-100 px-5 py-4'} data-testid="search-v2-grid">
          <div className="lg:hidden mb-2">
            <Button size="sm" variant="outline" onClick={() => setShowFiltersMobile(true)}>
              <Filter className="h-4 w-4 mr-1" />{t('searchV2.filters.open')}
            </Button>
          </div>
          {/* P8-A1 — centre column header: one clear owner for the result
              count, the treatment-details affordance and the presentation
              toggle, so the middle panel reads as its own region. */}
          <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
            <div className="min-w-0 flex items-center gap-2">
              <Search className="h-4 w-4 text-slate-400 flex-none" aria-hidden="true" />
              <div className="min-w-0">
              <p className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold leading-none">{t('searchV2.panels.results')}</p>
              <p className="text-xs text-slate-600" data-testid="search-v2-result-count">
                {loading
                  ? t('searchV2.results.searching')
                  : (!filters.treatment
                    ? t('searchV2.results.selectTreatment')
                    : t('searchV2.results.count', { count: providers.length }))}
              </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap min-w-0">
              {/* P7-B2 — opens the canonical published treatment record. */}
              {filters.treatment && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs max-w-full min-w-0"
                  onClick={() => setTreatmentDetailsOpen(true)}
                  data-testid="search-v2-treatment-details-btn"
                >
                  <Info className="h-3.5 w-3.5 mr-1.5 flex-none" />
                  <span className="truncate">{t('searchV2.results.about', { treatment: treatmentDisplayName || fmt(filters.treatment) })}</span>
                </Button>
              )}
              {/* P8-A3 — presentation toggle. */}
              <div
                className="inline-flex rounded-lg border border-slate-300 bg-white p-0.5"
                role="group"
                aria-label={t('searchV2.view.group')}
                data-testid="search-v2-view-mode-toggle"
              >
                {[
                  { id: 'list', label: t('searchV2.view.list'), icon: Rows3 },
                  { id: 'card', label: t('searchV2.view.cards'), icon: LayoutGrid },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setViewMode(m.id)}
                    aria-pressed={viewMode === m.id}
                    className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[#3BA4C5] ${
                      viewMode === m.id ? 'bg-slate-900 text-white' : 'text-slate-600 hover:text-slate-900'
                    }`}
                    data-testid={`search-v2-view-mode-${m.id}`}
                  >
                    <m.icon className="h-3.5 w-3.5" aria-hidden="true" />{m.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {viewMode === 'card' ? (
            <div data-testid="search-v2-card-list">
              {sortedProviders.length > 0 && (
                <div className="grid gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                  {sortedProviders.map((p) => (
                    <ProviderCard
                      key={p.clinic_id || p.provider_id}
                      provider={p}
                      selected={selected?.clinic_id === p.clinic_id}
                      onSelect={() => openProviderDetail(p)}
                      treatment={filters.treatment}
                      treatmentName={treatmentDisplayName}
                      categorySlugs={offerCategorySlugs}
                      shortlisted={shortlist.includes(p.clinic_id)}
                      onShortlistToggle={() => toggleShortlist(p.clinic_id)}
                      isSelectedForQuote={selectedClinicsV2.includes(p.clinic_id)}
                      onToggleSelectForQuote={() => toggleClinicSelectionV2(p.clinic_id)}
                    />
                  ))}
                </div>
              )}
              {!loading && sortedProviders.length === 0 && (
                <div className="rounded-xl border border-slate-200 bg-white">
                  {filters.shortlist_only === true && filters.treatment
                    ? (
                      <div className="text-center text-slate-500 text-sm py-12" data-testid="search-v2-empty">
                        {t('searchV2.empty.shortlistNoMatch')}
                      </div>
                    )
                    : <EmptyResults filters={filters} setFilters={setFilters} categoryLabel={activeCategoryLabel} treatmentName={treatmentDisplayName} />}
                </div>
              )}
            </div>
          ) : (
          <div className="rounded-xl border border-slate-200 bg-white overflow-hidden shadow-sm" data-testid="search-v2-tile-list">
            {/* Header column labels — pure scan affordance, no data.
                Rendered once, only in list mode, only when there are rows
                to label. */}
            {sortedProviders.length > 0 && (
              <div className="hidden md:flex sticky top-0 z-10 items-center gap-3 px-3 py-2 bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                <span className="w-10 flex-none" />
                <span className="flex-1">{t('searchV2.tile.provider')}</span>
                <span className="hidden md:block xl:hidden 2xl:block w-24 text-right">
                  {filters.treatment ? t('searchV2.tile.startingTreatment') : t('searchV2.tile.startingGlobal')}
                </span>
                <span className="hidden md:flex xl:hidden 2xl:flex items-center gap-3 flex-none">
                  {/* SV2-ACCEPT-01 — these four labels are reused by the
                      per-row metrics, so the TR values are kept short enough
                      to fit the 4rem column; the full wording lives in the
                      tooltips. */}
                  <span className="w-16 text-right" title={t('searchV2.tile.verifiedTooltip')}>{t('searchV2.tile.verified')}</span>
                  <span className="w-16 text-right" title={t('searchV2.tile.doctorsTooltip')}>{t('searchV2.tile.doctors')}</span>
                  <span className="w-16 text-right" title={t('searchV2.tile.languagesTooltip')}>{t('searchV2.tile.languages')}</span>
                  {/* SV2-OFFER-LABEL-01 — the offers column carries the full
                      "International Offers" wording, so it is wider than the
                      three numeric columns; the per-row micro-label under each
                      count stays short (full term in its tooltip) to keep the
                      row height unchanged. */}
                  <span className="w-24 text-right leading-tight" title={t('searchV2.tile.packagesTooltip')}>{t('searchV2.tile.packagesColumn')}</span>
                </span>
                <span className="flex-none w-7" />
                <span className="flex-none ml-1 w-9 text-right">{t('searchV2.tile.save')}</span>
              </div>
            )}
            {sortedProviders.map((p) => (
              <ProviderTile
                key={p.clinic_id || p.provider_id}
                provider={p}
                selected={selected?.clinic_id === p.clinic_id}
                onSelect={() => openProviderDetail(p)}
                treatment={filters.treatment}
                treatmentName={treatmentDisplayName}
                categorySlugs={offerCategorySlugs}
                shortlisted={shortlist.includes(p.clinic_id)}
                onShortlistToggle={() => toggleShortlist(p.clinic_id)}
                isSelectedForQuote={selectedClinicsV2.includes(p.clinic_id)}
                onToggleSelectForQuote={() => toggleClinicSelectionV2(p.clinic_id)}
              />
            ))}
            {!loading && sortedProviders.length === 0 && (
              filters.shortlist_only === true && filters.treatment
                ? (
                  <div className="text-center text-slate-500 text-sm py-12" data-testid="search-v2-empty">
                    {t('searchV2.empty.shortlistNoMatch')}
                  </div>
                )
                : <EmptyResults filters={filters} setFilters={setFilters} categoryLabel={activeCategoryLabel} treatmentName={treatmentDisplayName} />
            )}
          </div>
          )}
          {/* SEARCH-V2-REQUEST-QUOTE-PARITY-01 — sticky quote-request bar.
              Renders only when selectedClinicsV2.length > 0. */}
          {selectedClinicsV2.length > 0 && !!filters.treatment && (
            <>
              <p
                className="text-[11px] text-slate-500 text-center mt-3 mb-1"
                data-testid="search-v2-decision-helper-hint"
              >
                {t('searchV2.selection.hint')}
              </p>
              <div
                className="sticky bottom-0 z-20 bg-white border-t border-slate-200 shadow-lg p-3 rounded-t-xl"
                data-testid="search-v2-selected-providers-sticky-bar"
              >
                <div className="max-w-4xl mx-auto space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div
                        className="text-sm font-semibold text-slate-900"
                        data-testid="search-v2-selected-count"
                      >
                        {t('searchV2.selection.mySelection', { count: selectedClinicsV2.length })} — {t('searchV2.selection.ready', { count: selectedClinicsV2.length })}
                      </div>
                      <div className="mt-1 flex flex-wrap gap-1.5" data-testid="search-v2-selected-provider-chips">
                        {selectedClinicsV2.map((pid) => {
                          const c = providers.find((x) => x.clinic_id === pid);
                          const label = c?.clinic_name || pid;
                          return (
                            <div
                              key={pid}
                              className="inline-flex items-center gap-1.5 bg-blue-50 border border-blue-200 rounded-md px-2 py-0.5"
                              data-testid={`search-v2-selected-chip-${pid}`}
                            >
                              <span className="text-xs font-semibold text-blue-900 truncate max-w-[180px]" title={label}>
                                {label}
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleClinicSelectionV2(pid)}
                                className="text-blue-700 hover:text-blue-900 font-bold leading-none"
                                aria-label={`Remove ${label}`}
                                data-testid={`search-v2-selected-chip-remove-${pid}`}
                              >×</button>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={clearSelectedProvidersV2}
                      className="flex-shrink-0 h-7 text-xs px-2"
                      data-testid="search-v2-clear-selected-btn"
                    >{t('searchV2.selection.clearAll')}</Button>
                  </div>
                  {isAuth && (
                    <div className="pt-1 border-t border-slate-100">
                      {/* SSO-A1/A3 — state WHAT is being requested from each
                          provider before it is sent: the standard treatment
                          unless the patient clicked an International Offer,
                          plus any non-binding doctor preference. */}
                      <ul className="mb-2 space-y-0.5" data-testid="search-v2-selection-summary">
                        {selectedClinicsV2.map((pid) => {
                          const prov = providers.find((p) => p.clinic_id === pid);
                          const pkg = pendingPackageByClinic[pid];
                          const doc = preferredDoctorByClinic[pid];
                          return (
                            <li key={pid} className="text-[10px] text-slate-500 truncate" data-testid={`search-v2-selection-summary-${pid}`}>
                              <span className="font-medium text-slate-600">{prov?.clinic_name || pid}</span>
                              {' · '}
                              <span data-testid={`search-v2-selection-mode-${pid}`}>
                                {pkg?.campaign_id
                                  ? t('searchV2.selection.offerSelected', { title: pkg.title || '' })
                                  : t('searchV2.selection.standardTreatment')}
                              </span>
                              {doc?.doctor_id && (
                                <span data-testid={`search-v2-selection-doctor-${pid}`}>
                                  {' · '}{t('searchV2.selection.doctorPreference', { name: doc.full_name })}
                                </span>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                      <label htmlFor="search-v2-patient-inquiry-note" className="block text-[11px] font-semibold text-slate-600 mb-1">
                        {t('searchV2.selection.addNote')}
                      </label>
                      <textarea
                        id="search-v2-patient-inquiry-note"
                        rows={2}
                        maxLength={PATIENT_INQUIRY_NOTE_MAX_V2}
                        value={patientInquiryNote}
                        onChange={(e) => setPatientInquiryNote(e.target.value.slice(0, PATIENT_INQUIRY_NOTE_MAX_V2))}
                        placeholder={t('searchV2.selection.notePlaceholder')}
                        className="w-full text-xs border border-slate-200 rounded-md px-2 py-1.5 focus:outline-none focus:border-slate-400 resize-none"
                        data-testid="search-v2-patient-inquiry-note"
                      />
                      <div className="text-[10px] text-slate-400 text-right">{patientInquiryNote.length}/{PATIENT_INQUIRY_NOTE_MAX_V2}</div>
                    </div>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-[11px] text-slate-500 italic" data-testid="search-v2-offer-flow-helper-text">
                      {!filters.treatment
                        ? t('searchV2.selection.helperSelectTreatment')
                        : requestNotAccepted
                        ? t('searchV2.selection.notRequestable')
                        : requestEligibilityUnknown
                        ? t('searchV2.selection.eligibilityUnknown')
                        : isAuth
                        ? t('searchV2.selection.helperProviderResponse')
                        : t('searchV2.selection.helperLoginPrompt')}
                    </p>
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleSubmitRequestsV2}
                      disabled={submittingRequests || selectedClinicsV2.length === 0 || !filters.treatment || requestNotAccepted}
                      className="bg-blue-600 hover:bg-blue-700 text-white h-8 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="search-v2-request-offers-cta"
                      title={!filters.treatment
                        ? t('searchV2.toast.selectTreatmentFirst')
                        : requestNotAccepted ? t('searchV2.selection.notRequestable') : undefined}
                    >{submittingRequests ? t('searchV2.selection.sending') : t('searchV2.selection.requestCta')}</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </main>

        {/* P8-A2 — the former 28px toggle rail lived here. It rendered as a
            bare chevron on a white strip between two white panels and was
            reported invisible by the owner. The single canonical control
            now lives in the detail-panel header (same state, same
            `search-v2-center-collapse-toggle` test id, visible in both
            states, labelled, keyboard reachable). */}

        {/* STABILIZATION-01: DESKTOP DETAIL COLUMN — always mounted at
            xl+ (SV2-IPAD-LAYOUT-01), never dismissable.  onClose is intentionally omitted
            so the X close button does not render. */}
        <div className={centerCollapsed ? 'hidden xl:flex flex-1 min-w-0' : 'hidden xl:flex'}>
          <ProviderDetailPanel
            provider={selected}
            treatment={filters.treatment}
            taxonomy={taxonomy}
            onOpenDoctor={(d) => setDoctorModal({ doctor: d, clinic: selected })}
            onPreferDoctor={handlePreferDoctor}
            preferredDoctorId={preferredDoctorByClinic[selected?.clinic_id]?.doctor_id || null}
            onOpenAcc={(accs, index) => setAccViewer({ accs, index })}
            onOpenGallery={(items, index) => setGalleryViewer({ items, index })}
            onRequest={handleRequest}
            expanded={centerCollapsed}
            onToggleExpanded={() => setCenterCollapsed((v) => !v)}
          />
        </div>
      </div>

      {/* STABILIZATION-01: MOBILE DETAIL OVERLAY — full-screen drill-in,
          dismissable via X.  Mounts only on tap, exclusive with the
          desktop mount via the Tailwind `lg:` breakpoint.
          P8-A1 — the overlay must sit ABOVE the global `fixed top-0 z-50`
          app header (components/Header.jsx L125). At z-40 the panel header
          — and with it the only X close control — rendered underneath the
          nav and was unreachable. */}
      {selected && mobileDetailOpen && (
        <div className="xl:hidden fixed inset-0 z-[60] bg-white flex flex-col" data-testid="search-v2-detail-mobile">
          <ProviderDetailPanel
            provider={selected}
            treatment={filters.treatment}
            taxonomy={taxonomy}
            onOpenDoctor={(d) => setDoctorModal({ doctor: d, clinic: selected })}
            onPreferDoctor={handlePreferDoctor}
            preferredDoctorId={preferredDoctorByClinic[selected?.clinic_id]?.doctor_id || null}
            onOpenAcc={(accs, index) => setAccViewer({ accs, index })}
            onOpenGallery={(items, index) => setGalleryViewer({ items, index })}
            onRequest={handleRequest}
            onClose={() => setMobileDetailOpen(false)}
          />
        </div>
      )}

      <DoctorDetailModal
        open={!!doctorModal}
        doctor={doctorModal?.doctor || null}
        clinic={doctorModal?.clinic || null}
        treatment={filters.treatment}
        onClose={() => setDoctorModal(null)}
        onRequest={() => {
          // SEARCH-V2-REQUEST-QUOTE-PARITY-01 — Doctor modal "Request offer"
          // adds this doctor's clinic to selectedClinicsV2 (additive, no
          // duplicate) and closes the modal.  Does NOT touch shortlist.
          // Actual POST happens only from the sticky bar CTA.
          const cid = doctorModal?.clinic?.clinic_id;
          if (cid) {
            setSelectedClinicsV2((prev) => (prev.includes(cid) ? prev : [...prev, cid]));
          }
          setDoctorModal(null);
        }}
      />

      <AccreditationViewer
        open={!!accViewer}
        accs={accViewer?.accs || []}
        index={accViewer?.index || 0}
        onIndex={(i) => setAccViewer((p) => p ? { ...p, index: i } : p)}
        onClose={() => setAccViewer(null)}
      />

      <GalleryLightbox
        open={!!galleryViewer}
        items={galleryViewer?.items || []}
        index={galleryViewer?.index || 0}
        onIndex={(i) => setGalleryViewer((p) => p ? { ...p, index: i } : p)}
        onClose={() => setGalleryViewer(null)}
      />

      <CompareModal
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        providers={shortlistedProviders.slice(0, 3)}
        treatment={filters.treatment}
        treatmentName={treatmentDisplayName}
      />

      <TreatmentDetailsModal
        open={treatmentDetailsOpen}
        slug={filters.treatment}
        provider={selected}
        onClose={() => setTreatmentDetailsOpen(false)}
      />
    </div>
  );
}
